/**
 * Slice LV-02 · useLeviCommand — session + MediaRecorder + submit lifecycle
 * for the shared conversational router at /api/levi/command.
 *
 * Slice LEVI-CONT-01 · adds two additive behaviors:
 *   1. Client-side session rehydration from localStorage +
 *      GET /api/levi/my-current-session. Restores useful context
 *      only. Never restores or executes pending work.
 *   2. Automatic endpoint detection via a lightweight RMS-based
 *      VAD (frontend/src/lib/leviVAD.js) that drives the existing
 *      stopAndSend() path. Manual Stop remains available.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { stopSpeaking, primeTts, speakLeviReply, playServerTtsForReply,
         cancelActiveSpeech, isSpeaking as leviIsSpeaking,
         subscribeSpeakingChange, pushDiag as pushSpeechDiag } from "@/lib/leviSpeech";
import { useLang } from "@/context/LangContext";
import { useLeviNavigation } from "@/hooks/useLeviNavigation";
import { readLeviWorkspaceSnapshot } from "@/lib/leviWorkspace";
import { createLeviVAD } from "@/lib/leviVAD";
import { createBargeInDetector } from "@/lib/leviBargeIn";
import { acquire as acquireWakeLock, release as releaseWakeLock, isHeld as isWakeLockHeld } from "@/lib/leviWakeLock";
import { derivePresence } from "@/lib/leviPresence";
import { isVisionLiveConversationEnabled } from "@/lib/leviVisionLiveFlag";
import { captureVisionSnapshot } from "@/lib/leviVisionSnapshot";
// PO 2026-08-13 · Fresh-Session QA Contract. Read-only import of the
// module-scope one-shot signal that indicates the current page load
// was launched with ``?leviFreshSession=1``. Consumed at module load
// in ``leviFreshSession.js`` — BEFORE React hooks mount — so that the
// prior stored session id is already removed from localStorage by
// the time this hook runs. This tiny guard also prevents the
// ``/levi/my-current-session`` fallback below from resurrecting the
// prior session for that page load. Does NOT change useLeviCommand's
// lifecycle: it is a single conditional inside an existing branch.
import { wasFreshSessionRequested } from "@/lib/leviFreshSession";

const CHUNK_MS = 800;
const SESSION_STORAGE_KEY = "jc.levi.session_id";
const pickMime = () => {
  if (typeof MediaRecorder === "undefined") return "";
  for (const m of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"]) {
    try { if (MediaRecorder.isTypeSupported(m)) return m; } catch { /* noop */ }
  }
  return "";
};

// Try to derive an active project or job id from the current route so
// Levi has context ("Levi, pull up the estimate" inside
// /app/projects/{id} OR /app/jobs/{id}). Both are legitimate active
// contexts; Levi's shared entity resolver understands either.
const readActiveProjectId = (pathname) => {
  const m = pathname && pathname.match(/\/app\/projects\/([A-Za-z0-9-]+)/);
  return m ? m[1] : null;
};
const readActiveJobId = (pathname) => {
  const m = pathname && pathname.match(/\/app\/jobs\/([A-Za-z0-9-]+)/);
  return m ? m[1] : null;
};

/**
 * Programmatic canonical Levi command entry — the reusable seam that lets
 * non-drawer surfaces (Dashboard "Show Levi the Job") invoke the SAME
 * `/api/levi/command` endpoint with the SAME `open_camera` envelope and
 * the SAME server-owned context-aware routing decision (`active_project_id`
 * → project-scoped Vision; no project → session-scoped Vision) that
 * conversational "Activate Vision" already uses inside `useLeviCommand`.
 *
 * This helper INTENTIONALLY does NOT duplicate the intent-resolution or
 * routing decision: it hands the text to the backend, and honors the
 * server's `open_camera` response — the exact same envelope the drawer
 * receives at L1377-1395 below. If the server ever changes routing, this
 * helper follows automatically because the server is the source of truth.
 *
 * Callers must supply the `navigate` handle from react-router — this
 * keeps navigation controllable by the calling page and avoids adding
 * a second router context.
 */
export async function submitLeviCommandProgrammatic({ text, language = "en", navigate }) {
  const fd = new FormData();
  fd.append("text", text);
  fd.append("language", language);
  const r = await api.post("/levi/command", fd,
    { headers: { "Content-Type": "multipart/form-data" } });
  const d = r?.data || {};
  // Reuse the SAME context-aware routing branch the drawer executes on
  // `open_camera` (see the hook body below at ~L1377). No duplication of
  // the decision itself — the server has already resolved it.
  if (d.action_type === "open_camera" && typeof navigate === "function") {
    try {
      const pid = d.active_project_id
                || (d.extra && d.extra.active_project_id)
                || d.project_id
                || null;
      const surface = d.extra?.surface || d.surface || "video_walk_recorder";
      if (surface === "video_walk_recorder_session_scoped") {
        const lsid = d.levi_estimate_session_id || d.extra?.levi_estimate_session_id || "";
        navigate(`/app/estimates/new/levi?levi_camera=1${lsid ? `&lsid=${encodeURIComponent(lsid)}` : ""}`);
      } else if (pid) {
        navigate(`/app/projects/${encodeURIComponent(pid)}?levi_vision=1`);
      } else {
        navigate(`/app/estimates/new/levi?levi_camera=1`);
      }
    } catch (e) {
      console.warn("[LEVI-VISION-PROGRAMMATIC] navigate failed:", e);
    }
  }
  return d;
}

export function useLeviCommand() {
  const { lang } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const { performNavigation } = useLeviNavigation();
  const [sessionId, setSessionId] = useState(null);
  const [turns, setTurns] = useState([]);
  const [status, setStatus] = useState("idle");
  // idle | listening | uploading | thinking | awaiting_confirmation |
  // executing | opened | sent | unclear | error
  const [error, setError] = useState("");
  const [pendingAction, setPendingAction] = useState(null);
  const [lastVisible, setLastVisible] = useState("");
  const [lastMatches, setLastMatches] = useState(null);
  const [lastExtra, setLastExtra] = useState(null);
  // Phase 7-B · Conversation Flow. `conversationActive` gates the
  // auto-listen loop. Explicit contractor activation is REQUIRED
  // (mic tap). Opening the drawer alone does NOT activate.
  const [conversationActive, setConversationActive] = useState(false);
  const [inactivityNotice, setInactivityNotice] = useState(false);
  // Phase 7-C · drawer-open observability + safety-event refs for
  // presence derivation. The `muted` state and its entry points
  // were retired after PO QA (2026-02) — see leviPresence.js note.
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [tabHidden, setTabHidden] = useState(
    typeof document !== "undefined" ? !!document.hidden : false);
  // QA-only diagnostic exposure (PO 2026-08-06). Mirrors selected
  // refs to ``window.__leviQAState`` so the LeviVoiceDiagPanel's
  // "Copy Full Diagnostics" button can capture them into a single
  // JSON blob. No conversation, microphone, voice, Vision, state, or
  // routing behavior changes. Read-only surface, gated by the
  // ?leviDiag=1 flag which already gates panel mounting. No network
  // transmission; no permanent analytics.
  useEffect(() => {
    const publish = () => {
      try {
        const s = streamRef.current;
        const rec = recRef.current;
        const tracks = (s && s.getTracks && s.getTracks()) || [];
        window.__leviQAState = {
          ts: Date.now(),
          session_id: sessionId,
          conversation_active: conversationActiveRef.current,
          status: statusRef.current,
          permission: permissionRef.current,
          drawer_open: drawerOpen,
          tab_hidden: tabHidden,
          stream: {
            present: !!s,
            active: !!(s && s.active),
            trackCount: tracks.length,
            tracks: tracks.map((t) => ({
              kind: t.kind, readyState: t.readyState,
              muted: !!t.muted, enabled: !!t.enabled,
            })),
          },
          recorder: {
            present: !!rec,
            state: (rec && rec.state) || null,
          },
          vad_present: !!vadRef.current,
          pending_action: pendingAction && pendingAction.action_type || null,
          // P0 · Wrapper Voice Boundary iPhone QA observability
          // (PO 2026-08-15). Full pending_action object PLUS the P0-
          // hotfix-relevant response-envelope fields, sourced from
          // ``lastExtraRef.current`` (last /api/levi/command reply)
          // and the current ``pendingAction`` state. Read-only mirror
          // of already-published values; no Levi/routing/prompt/model/
          // flag/Boundary-3 behavior is affected.
          pending_action_full: pendingAction || null,
          last_envelope: (() => {
            const ex = lastExtraRef.current || {};
            const _keep = [
              "action_type", "capability", "conversation_layer",
              "expects_reply", "expected_answer_type",
              "conversation_control", "wrapper_voice_source",
              "coworker_verdict", "coworker_model",
              "coworker_tools_used", "coworker_plan_ms",
              "coworker_tool_ms", "coworker_compose_ms",
              "coworker_wall_ms", "coworker_latency_ms",
              "coworker_mode", "coworker_mode_confidence",
              "coworker_mode_source", "coworker_stage1_ms",
              "coworker_stage2_ms",
              "boundary3_pending_action_kind",
              "boundary3_draft_ids",
              "boundary3_wrapper_statuses",
            ];
            const out = {};
            for (const k of _keep) {
              if (ex && Object.prototype.hasOwnProperty.call(ex, k)) {
                out[k] = ex[k];
              }
            }
            return out;
          })(),
          last_transcript: lastVisible || null,
          last_reply: (turns && turns.length && turns[turns.length - 1]
            && turns[turns.length - 1].reply) || null,
          turns_count: (turns || []).length,
          inactivity_notice: inactivityNotice || null,
          // P0 RC-2/RC-3 diagnostic additions (PO 2026-08-09). Exposes
          // the post-walk handoff channel so a failed real-iPhone
          // reproduction can be triaged to upload / transcription /
          // aggregation / handoff / rendering in ONE Copy Diagnostics
          // capture.
          post_walk: {
            pending_buffered: !!(typeof window !== "undefined"
                                 && window.__leviPendingPostWalk),
            buffered_walk_session_id: (typeof window !== "undefined"
                                       && window.__leviPendingPostWalk
                                       && window.__leviPendingPostWalk.walk_session_id) || null,
            buffered_at: (typeof window !== "undefined"
                          && window.__leviPendingPostWalk
                          && window.__leviPendingPostWalk.buffered_at) || null,
            active_vision_session_id: activeVisionSessionIdRef.current || null,
          },
          // P0 RC-4 (PO 2026-08-09 · two-diagnostic authoritative
          // lifecycle repair). Expose timer ownership so an illegal
          // inactivity shutdown is immediately obvious in the next
          // Copy Diagnostics blob.
          inactivity: {
            armed: !!inactivityTimerRef.current,
            armed_at: inactivityArmedAtRef.current || null,
            deadline_ms: inactivityDeadlineRef.current || null,
            remaining_ms: (inactivityDeadlineRef.current
              ? Math.max(0, inactivityDeadlineRef.current - Date.now())
              : null),
            last_reset_reason: lastInactivityResetReasonRef.current || null,
            // P0 RC-5 (PO 2026-08-09) · exposes the 5-minute idle
            // ceiling as PO-defined fields. The idle_timer_* fields
            // are named per the PO's Step 8 spec so future Copy
            // Diagnostics captures make illegal shutdowns
            // immediately obvious.
            idle_timer_duration_ms: 300000,
            idle_timer_armed: !!inactivityTimerRef.current,
            idle_started_at: inactivityArmedAtRef.current || null,
            idle_deadline: inactivityDeadlineRef.current || null,
            idle_remaining_ms: (inactivityDeadlineRef.current
              ? Math.max(0, inactivityDeadlineRef.current - Date.now())
              : null),
            idle_suspended: (() => {
              try { return _leviIsActiveWork().active; } catch { return false; }
            })(),
            idle_suspended_reason: (() => {
              try { return _leviIsActiveWork().reason; } catch { return null; }
            })(),
          },
          active_work_state: (() => {
            try {
              const a = _leviIsActiveWork();
              return a.active ? a.reason : "idle";
            } catch { return "idle"; }
          })(),
          vision: (typeof window !== "undefined"
                   && window.__leviVisionState) || null,
          turn: {
            levi_speaking: (() => {
              try { return !!leviIsSpeaking(); } catch { return false; }
            })(),
            contractor_speaking: !!contractorSpeakingRef.current,
            owner: (() => {
              // P0 RC-5-follow-up · leviTurnInProgress takes
              // precedence over physical TTS state so the diagnostic
              // owner accurately reflects the logical turn during
              // the 503 fallback race window.
              if (leviTurnInProgressRef.current) return "levi_speaking";
              let _l = false;
              try { _l = !!leviIsSpeaking(); } catch { /* noop */ }
              if (_l) return "levi_speaking";
              if (contractorSpeakingRef.current) return "contractor_speaking";
              if (statusRef.current === "thinking") return "processing";
              if (statusRef.current === "listening") return "waiting_for_contractor";
              try {
                const vs = window.__leviVisionState;
                if (vs && vs.active) return `vision_${vs.status || "active"}`;
              } catch { /* noop */ }
              return "idle";
            })(),
          },
          // P0 RC-5-follow-up · explicit Levi-turn logical state,
          // authoritative source for the 503 → local-fallback race
          // window. Do not infer Levi's turn state from
          // speechSynthesis alone; read `levi_turn.in_progress`.
          levi_turn: {
            in_progress: !!leviTurnInProgressRef.current,
            started_at: leviTurnStartedAtRef.current || null,
            completion_reason: leviTurnCompletionReasonRef.current || null,
            canceled_reason: leviTurnCancelReasonRef.current || null,
          },
          listening: {
            epoch: listenEpochRef.current || 0,
            started_at: listenEpochStartedAtRef.current || null,
            stopped_at: listenEpochStoppedAtRef.current || null,
            stop_reason: listenEpochStopReasonRef.current || null,
            voice_ever: !!listenEpochVoiceEverRef.current,
            stale_ignored_count: listenEpochStaleIgnoredRef.current || 0,
            active: !!vadRef.current,
            // P0 (PO 2026-08-09) · VAD-level diagnostics for iOS
            // continuous-speech investigation. Read-only snapshot
            // published by the single authoritative WebAudio branch.
            vad: (() => {
              try {
                if (vadRef.current && typeof vadRef.current.getDiagnostics === "function") {
                  return vadRef.current.getDiagnostics();
                }
              } catch { /* noop */ }
              return null;
            })(),
          },
          wake_lock: {
            supported: (typeof navigator !== "undefined"
                        && !!navigator.wakeLock),
            held: (() => { try { return !!isWakeLockHeld(); } catch { return false; } })(),
          },
          // P0 Tier-2 (PO 2026-08-09) · voice keep-alive surface.
          // Owned by LeviListeningVisualizer, published to
          // `window.__leviVoiceKeepAlive`. Mirrored here so a single
          // Copy Diagnostics blob shows whether the synthetic media
          // element was actually alive when iOS reached Auto-Lock.
          voice_keepalive: (typeof window !== "undefined"
                            && window.__leviVoiceKeepAlive) || {
            active: false,
            media_element_playing: false,
            synthetic_stream_active: false,
            started_at: null,
            ended_at: null,
            end_reason: null,
          },
          visibility: {
            hidden: !!tabHidden,
            visibilityState: (typeof document !== "undefined" ? document.visibilityState : null),
          },
        };
      } catch { /* noop */ }
    };
    publish();
    const id = setInterval(publish, 500);
    return () => { try { clearInterval(id); } catch { /* noop */ } };
  }, [sessionId, drawerOpen, tabHidden, pendingAction, lastVisible, turns,
      inactivityNotice]);

  const [micPermissionDenied, setMicPermissionDenied] = useState(false);
  // C-2 · Speaking-state render bridge (2026-08-06). A dedicated
  // useState so that browser-TTS / server-audio start-and-end
  // transitions trigger a React re-render. This drives the presence
  // useMemo to re-evaluate — `leviIsSpeaking()` inside the useMemo
  // remains the authoritative source of truth. This bridge does NOT
  // change WHEN Levi speaks, WHEN he stops, WHETHER the mic re-arms,
  // WHETHER the conversation continues, or WHETHER it ends.
  const [_speakingTick, setSpeakingTick] = useState(false);
  useEffect(() => {
    const unsub = subscribeSpeakingChange((isNow) => {
      try { setSpeakingTick(!!isNow); } catch { /* noop */ }
    });
    return () => { try { unsub && unsub(); } catch { /* noop */ } };
  }, []);
  // P-Diag (PO 2026-08-06) · QA-safe one-shot environment
  // fingerprint. Logs at most one line per hook mount to
  // ``window.console`` so field QA can copy the exact device /
  // browser / API-support / voice-inventory context per session.
  // No persistent analytics, no network egress, no PII.
  useEffect(() => {
    try {
      const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
      const platform = (typeof navigator !== "undefined" && navigator.platform) || "";
      const languages = (typeof navigator !== "undefined" && navigator.languages) || [];
      const mediaDevicesOk =
        typeof navigator !== "undefined" &&
        !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
      const mediaRecorderOk = typeof MediaRecorder !== "undefined";
      const speechSynthesisOk =
        typeof window !== "undefined" &&
        !!(window.speechSynthesis && typeof window.speechSynthesis.speak === "function");
      // Voice inventory: available voices count + a compact list of
      // the top 8 names + lang tags so P-1 selection can be verified
      // during real-device QA.
      let voices = [];
      try {
        const raw = window.speechSynthesis && window.speechSynthesis.getVoices
          ? window.speechSynthesis.getVoices() : [];
        voices = raw.slice(0, 12).map((v) => ({
          name: v && v.name, lang: v && v.lang,
          localService: !!(v && v.localService),
          default: !!(v && v.default),
        }));
      } catch { /* noop */ }
      const isIOS = /iP(hone|ad|od)/.test(ua) || (platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1);
      const isAndroid = /Android/.test(ua);
      const isMac = /Macintosh|Mac OS X/.test(ua) && !isIOS;
      const isWindows = /Windows NT/.test(ua);
      const isFirefox = /Firefox\//.test(ua);
      const isSafari = /Safari\//.test(ua) && !/Chrome\//.test(ua) && !isFirefox;
      const isChromium = /Chrome\//.test(ua) && !isFirefox;
      const _fp = {
        marker: "P-Diag/2026-08-06",
        ua_head: ua.slice(0, 240),
        platform, languages_top: (languages || []).slice(0, 3),
        family: {
          isIOS, isAndroid, isMac, isWindows,
          isFirefox, isSafari, isChromium,
        },
        api: { mediaDevicesOk, mediaRecorderOk, speechSynthesisOk },
        voice_count_visible: voices.length,
        voice_head: voices,
      };
      try { console.log("[P-Diag]", JSON.stringify(_fp)); } catch { /* noop */ }
      try {
        window.__leviParityDiag = _fp;
      } catch { /* noop */ }
    } catch { /* noop */ }
  }, []);
  const bargeInRef = useRef(null);
  // Checkpoint 2 (2026-08-06) — remember the active Video Walk
  // session id so Levi's server-side ``vision_objective`` can be
  // told about it, and so ``vision_finalize_request`` /
  // ``vision_cancel_request`` / ``vision_pause_request`` /
  // ``vision_resume_request`` action envelopes can be dispatched
  // back to the recorder as window events. Reset to null when the
  // walk stops.
  const activeVisionSessionIdRef = useRef(null);
  // Vision Conversational Deployment · C1 · stream-ownership.
  // True when the mic MediaStream currently held in streamRef was
  // acquired by useVideoWalkSession (Vision) and is being consumed
  // by Levi's VAD/transcription. Levi must NOT stop those tracks on
  // its own teardown; Vision remains responsible for final teardown.
  const visionOwnedStreamRef = useRef(false);

  const streamRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  // LEVI-CONT-01 · additive refs
  const vadRef = useRef(null);
  const stopInFlightRef = useRef(false);
  // Phase 7-B · Conversation Flow refs (ref-mirrors of the state
  // above so the async onEnd callback captured at speak-time can
  // still consult the latest values).
  const conversationActiveRef = useRef(false);
  useEffect(() => { conversationActiveRef.current = conversationActive; },
                    [conversationActive]);
  const lastExtraRef = useRef(null);
  useEffect(() => { lastExtraRef.current = lastExtra; }, [lastExtra]);
  const statusRef = useRef("idle");
  useEffect(() => { statusRef.current = status; }, [status]);
  const permissionRef = useRef("prompt");  // 'granted' | 'denied' | 'prompt'
  const inactivityTimerRef = useRef(null);
  // P0 RC-4 (PO 2026-08-09) · authoritative turn-lifecycle refs.
  // Track whether the contractor is currently speaking so the long
  // inactivity timer's fire-time guard can suspend rather than
  // terminate. `leviIsSpeaking()` from leviSpeech is the authoritative
  // read for Levi's TTS state — no matching ref is needed.
  const contractorSpeakingRef = useRef(false);
  const inactivityArmedAtRef = useRef(null);
  const inactivityDeadlineRef = useRef(null);
  const lastInactivityResetReasonRef = useRef(null);
  // P0 RC-5-follow-up (PO 2026-08-09 · Emergent support race
  // condition). Explicit logical Levi-turn-in-progress flag. Set
  // TRUE the instant we commit to a Levi response — BEFORE any
  // server-TTS request goes out — and cleared ONLY when the
  // spoken/logical turn actually completes or is intentionally
  // cancelled. Bridges the specific race Emergent flagged:
  //   * primary/server TTS returns 503
  //   * browser/local fallback hasn't physically started speaking
  //   * `speechSynthesis.speaking` is momentarily false
  //   * inactivity guard would otherwise conclude Levi is idle
  // The `_leviIsActiveWork` guard treats leviTurnInProgress as
  // AUTHORITATIVE active work, so the idle timer cannot fire in
  // that gap.
  const leviTurnInProgressRef = useRef(false);
  const leviTurnStartedAtRef = useRef(null);
  const leviTurnCompletionReasonRef = useRef(null);
  const leviTurnCancelReasonRef = useRef(null);
  // P0 RC-6 (PO 2026-08-09) · listening epoch. Each `startListening`
  // increments the epoch and the VAD callbacks capture the current
  // value in-closure. Callbacks whose captured epoch no longer
  // matches `listenEpochRef.current` are STALE and MUST be ignored —
  // this prevents an earlier listening session's `no_voice_detected`
  // endpoint from firing into a later Levi turn (the exact pattern
  // observed in the PO diagnostic: stale VAD endpoint → stopAndSend
  // → cancel Levi mid-utterance).
  const listenEpochRef = useRef(0);
  const listenEpochStartedAtRef = useRef(null);
  const listenEpochStoppedAtRef = useRef(null);
  const listenEpochStopReasonRef = useRef(null);
  const listenEpochVoiceEverRef = useRef(false);
  const listenEpochStaleIgnoredRef = useRef(0);

  // PO 2026-08-13 · Duplicate-owner lifecycle repair (Boundary A).
  //
  // The read-only iPhone forensic proved that four delayed callbacks
  // in this hook can survive component unmount and later invoke
  // ``startListeningRef.current()`` (or submit work) on the OUTGOING
  // hook instance after a fresh instance has mounted. This creates
  // two live copies of the SAME ``useLeviCommand`` lifecycle sharing
  // the same window/localStorage state — the class of failure the
  // Product Owner heard on-device as "two Levi voices fighting".
  //
  // Each setTimeout id is now captured into a stable ref so the
  // unmount cleanup at the bottom of this hook can clearTimeout each
  // and prevent post-unmount resurrection. This is a pure lifecycle
  // correction — behavior while mounted is unchanged.
  const phase7cArmTimerRef = useRef(null);
  const emptyChunkArmTimerRef = useRef(null);
  const postWalkArmTimerRef = useRef(null);
  const bufferedPostWalkArmTimerRef = useRef(null);

  // PO 2026-08-13 · Duplicate-owner lifecycle repair (Boundary B).
  //
  // The forensic proved ``submit``'s identity churns on every route
  // navigation because it captures ``location.pathname`` in its
  // useCallback dep array. That identity churn re-runs the post-walk
  // effect below and schedules additional untracked setTimeouts on
  // every page change. Reading pathname from a ref instead keeps
  // ``submit``'s identity stable; the value read is always the
  // freshest ``location.pathname`` because this tiny mirror effect
  // updates the ref whenever the router route changes.
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);


  const _beginLeviTurn = useCallback((source) => {
    leviTurnInProgressRef.current = true;
    leviTurnStartedAtRef.current = Date.now();
    leviTurnCompletionReasonRef.current = null;
    leviTurnCancelReasonRef.current = null;
    try { pushSpeechDiag("leviTurn:begin", { source: source || "unknown" }); }
    catch { /* noop */ }
  }, []);

  const _endLeviTurn = useCallback((reason, kind) => {
    // kind: "completion" | "cancelation". Idempotent — repeated
    // calls do not re-fire diagnostics.
    if (!leviTurnInProgressRef.current) return;
    leviTurnInProgressRef.current = false;
    if (kind === "cancelation") {
      leviTurnCancelReasonRef.current = reason || "canceled";
    } else {
      leviTurnCompletionReasonRef.current = reason || "completed";
    }
    try {
      pushSpeechDiag("leviTurn:end", {
        kind: kind || "completion",
        reason: reason || "unspecified",
      });
    } catch { /* noop */ }
  }, []);

  const CONVERSATION_INACTIVITY_MS = 300000; // 5 minutes — TRUE-IDLE
  // ceiling (PO 2026-08-09 RC-5 correction). The prior 30-second
  // window was too short for real contractor field workflows
  // (Vision walkthroughs run 2–5+ minutes; long scope explanations,
  // moving between rooms, upload/transcription/aggregate processing
  // all count as ACTIVE work, not inactivity). This timer must
  // ONLY count during genuine idle — see the fire-time guard in
  // `_resetInactivityTimer` below, which suspends and re-arms
  // whenever any of the active-work signals are hot:
  //   * Levi speaking          (leviIsSpeaking())
  //   * Contractor speaking    (contractorSpeakingRef)
  //   * Status "thinking"      (command round-trip in flight)
  //   * Vision active          (window.__leviVisionState.active)
  //   * Post-walk buffered     (window.__leviPendingPostWalk)
  // The 8-second `NOVOICE_GRACE_MS` inside leviVAD remains
  // orthogonal — that's the short post-Levi listening window, not
  // the long idle ceiling.
  const _leviIsActiveWork = () => {
    // P0 RC-5-follow-up · leviTurnInProgress is the AUTHORITATIVE
    // Levi-active signal, checked FIRST so it bridges the server-TTS
    // 503 → local fallback race window when the browser's own
    // `speechSynthesis.speaking` is momentarily false.
    if (leviTurnInProgressRef.current) {
      return { active: true, reason: "levi_turn_in_progress" };
    }
    let _l = false;
    try { _l = leviIsSpeaking(); } catch { /* noop */ }
    if (_l) return { active: true, reason: "levi_speaking" };
    if (contractorSpeakingRef.current) {
      return { active: true, reason: "contractor_speaking" };
    }
    if (statusRef.current === "thinking") {
      return { active: true, reason: "command_processing" };
    }
    try {
      const vs = (typeof window !== "undefined") ? window.__leviVisionState : null;
      if (vs && vs.active) {
        return { active: true, reason: `vision_${vs.status || "active"}` };
      }
    } catch { /* noop */ }
    try {
      if (typeof window !== "undefined" && window.__leviPendingPostWalk) {
        return { active: true, reason: "post_walk_handoff_pending" };
      }
    } catch { /* noop */ }
    return { active: false, reason: null };
  };
  // Continuous Conversation contract (PO 2026-08-02 correction).
  // Empty-chunks (contractor tapped mic but VAD never heard voice)
  // must NOT dead-end the loop. Auto-retry up to this ceiling; then
  // yield gracefully to Tap-to-Speak. The counter resets whenever a
  // successful (non-empty) chunk is sent or the contractor ends.
  //
  // PO recorder-lifecycle report 2026-08-04 — reduced from 3 → 1.
  // Three consecutive "I didn't catch that" cues felt desperate;
  // one honest retry, then hand control back to the contractor.
  const emptyChunkRetriesRef = useRef(0);
  const MAX_EMPTY_CHUNK_RETRIES = 1;

  // Recorder-lifecycle safety watchdog (PO 2026-08-04). Force-
  // releases the mic if any single listening session runs past
  // ``LISTEN_HARD_CEILING_MS`` without endpointing naturally. This
  // is a last-line defense against upstream callback failures
  // (VAD frame skip, browser TTS onend never firing, tab-focus
  // races) that could otherwise strand the recorder in ``listening``
  // indefinitely. Idempotent; the ref is cleared by _releaseMic.
  const listenWatchdogRef = useRef(null);
  // P0 REPAIR-3 (PO 2026-08-09) · aligned with the VAD's own
  // `SAFETY_MAX_LISTEN_MS = 120000`. The prior 60_000 value predated
  // the VAD safety ceiling and was firing FIRST during normal
  // continuous contractor narration — the watchdog then called
  // `stopAndSend` directly, bypassing VAD's `onEndpoint` callback and
  // producing a rogue submit at ~60 s with no `contractorVoice:end`.
  // The VAD's silence-hang / safety-ceiling remains the primary
  // endpoint mechanism; this watchdog is now genuinely a last-line
  // defense (VAD dies, rAF halts, etc.).
  const LISTEN_HARD_CEILING_MS = 120000;
  // P0 REPAIR-3 (PO 2026-08-09) · submit-attribution ref. Any caller
  // that invokes stopAndSend (VAD endpoint, watchdog, UI Stop, empty-
  // chunk retry, listen-watchdog fallback) may stamp this ref BEFORE
  // calling so the submit() diagnostic can identify who initiated
  // the finalization. Never mutates behavior; read-only diagnostic.
  const lastStopReasonRef = useRef({ reason: null, source: null, at: null });
  const _clearListenWatchdog = useCallback(() => {
    if (listenWatchdogRef.current) {
      try { clearTimeout(listenWatchdogRef.current); }
      catch { /* noop */ }
      listenWatchdogRef.current = null;
    }
  }, []);

  // P-2 (PO 2026-08-06) · Stream health probe. iOS Safari may leave
  // ``streamRef.current`` truthy but with all tracks in
  // ``readyState === "ended"`` after backgrounding / permission
  // revoke / long TTS playback. Reusing a dead stream would silently
  // capture nothing. This helper lets startListening decide whether
  // to reuse or freshly request. Rendering-safe; no side effects.
  const _hasHealthyStream = useCallback(() => {
    try {
      const s = streamRef.current;
      if (!s) return false;
      if (typeof s.active === "boolean" && !s.active) return false;
      const tracks = s.getTracks ? s.getTracks() : [];
      if (!tracks.length) return false;
      return tracks.every((t) => t && t.readyState === "live");
    } catch { return false; }
  }, []);

  // Fully release the microphone stream + VAD. Idempotent.
  // P-2 (PO 2026-08-06) · Added ``preserveStream`` for the iOS
  // continuous-conversation parity: intra-conversation turn boundaries
  // (auto re-arm, empty-chunk retry, listen-watchdog force-close where
  // the loop is still active) can request that the stream tracks NOT
  // be stopped so the next getUserMedia is avoided (iOS Safari denies
  // getUserMedia outside a fresh user gesture).
  //
  // Final teardown paths (endConversation, pauseConversation, unmount
  // cleanup, permission-denied fallback, drawer close, tab hidden,
  // Vision walk started) MUST call with the default {} so the stream
  // is fully torn down and no background microphone remains.
  const _releaseMic = useCallback((opts) => {
    const preserveStream = !!(opts && opts.preserveStream);
    // Vision Conversational Deployment · C1 · when the current mic
    // stream is Vision-owned, ALWAYS preserve it and never stop the
    // tracks. Vision (useVideoWalkSession.stopStream) remains the
    // sole authority on final teardown. This override is placed at
    // the top of _releaseMic so every caller (endConversation,
    // pauseConversation, unmount cleanup, tab-hidden, permission-
    // denied, listen-watchdog, empty-chunk retry, intra-turn auto
    // re-arm) is uniformly Vision-safe without any caller-side
    // knowledge.
    const _visionOwned = !!visionOwnedStreamRef.current;
    // P0 RC-6 · invalidate the current listening epoch so any
    // in-flight VAD callbacks fire into a stale-ignored branch.
    if (listenEpochRef.current) {
      listenEpochStoppedAtRef.current = Date.now();
      listenEpochStopReasonRef.current = (opts && opts.stop_reason) || "release_mic";
      try {
        pushSpeechDiag("listening:epoch_end", {
          epoch: listenEpochRef.current,
          reason: listenEpochStopReasonRef.current,
          voiceEver: listenEpochVoiceEverRef.current,
        });
      } catch { /* noop */ }
      listenEpochRef.current = listenEpochRef.current + 1;
    }
    try { vadRef.current && vadRef.current.dispose(); } catch { /* noop */ }
    vadRef.current = null;
    try {
      if (recRef.current && recRef.current.state !== "inactive") {
        recRef.current.stop();
      }
    } catch { /* noop */ }
    recRef.current = null;
    if (!preserveStream && !_visionOwned) {
      try {
        const s = streamRef.current;
        if (s && s.getTracks) s.getTracks().forEach((t) => { try { t.stop(); } catch { /* noop */ } });
      } catch { /* noop */ }
      streamRef.current = null;
    } else if (_visionOwned) {
      // C1 · Vision-owned: clear the local ref so a subsequent
      // startListening re-checks window.__leviVisionMediaStream, but
      // NEVER stop the tracks — Vision still consumes them for its
      // own MediaRecorder.
      streamRef.current = null;
    }
    // Recorder-lifecycle safety (PO 2026-08-04). Clear any pending
    // hard-ceiling watchdog so a subsequent listen session starts
    // with a fresh timer window.
    if (listenWatchdogRef.current) {
      try { clearTimeout(listenWatchdogRef.current); }
      catch { /* noop */ }
      listenWatchdogRef.current = null;
    }
    // Vision Conversational Deployment · C1 · reset ownership flag
    // AFTER teardown so any pending closures observing "Vision owns
    // this stream" see a coherent snapshot. Next startListening will
    // re-establish ownership if Vision is still active.
    visionOwnedStreamRef.current = false;
  }, []);

  // Phase 7-C · barge-in dispose. Declared BEFORE endConversation
  // so its useCallback dep array captures a defined value. Idempotent.
  const _disposeBargeIn = useCallback(() => {
    if (bargeInRef.current) {
      try { bargeInRef.current.dispose(); } catch { /* noop */ }
      bargeInRef.current = null;
    }
  }, []);

  const _clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      try { clearTimeout(inactivityTimerRef.current); } catch { /* noop */ }
      inactivityTimerRef.current = null;
    }
    inactivityArmedAtRef.current = null;
    inactivityDeadlineRef.current = null;
  }, []);

  const endConversation = useCallback((opts = {}) => {
    if (!conversationActiveRef.current && !opts._force) {
      // Idempotent: already ended.
      return;
    }
    _clearInactivityTimer();
    // Reset the empty-chunk retry counter so a fresh conversation
    // starts clean.
    emptyChunkRetriesRef.current = 0;
    // P0 RC-5-follow-up · close any in-flight Levi turn cleanly.
    try { _endLeviTurn(opts.reason || "endConversation", "cancelation"); }
    catch { /* noop */ }
    // Phase 7-C · cancel any active speech first, then release mic.
    // cancelActiveSpeech is safe to call repeatedly.
    try { cancelActiveSpeech("conversation_ended"); } catch { /* noop */ }
    stopSpeaking("endConversation");
    _disposeBargeIn();
    _releaseMic();
    setConversationActive(false);
    conversationActiveRef.current = false;
    setStatus((s) => (s === "listening" ? "idle" : s));
    if (opts.reason === "inactivity") {
      setInactivityNotice(true);
      const _msg = (lang === "es")
        ? "Sin problema. Retomamos cuando quieras."
        : "No worries. We'll pick this back up whenever you're ready.";
      try {
        speakLeviReply(_msg, lang || "en", {
          source: "normal_reply", mode: "command", status: "idle",
        });
      } catch { /* noop */ }
    } else {
      setInactivityNotice(false);
    }
    // B (PO 2026-08-07) · release any held Screen Wake Lock. Safe
    // no-op if none was ever acquired or if the browser already
    // released it via visibilitychange.
    try { pushSpeechDiag("wakeLock:release_context", { source: "endConversation", reason: opts?.reason || null }); } catch { /* noop */ }
    try { releaseWakeLock("endConversation"); } catch { /* noop */ }
  }, [_clearInactivityTimer, _disposeBargeIn, _releaseMic, lang]);

  const pauseConversation = useCallback(() => {
    _clearInactivityTimer();
    emptyChunkRetriesRef.current = 0;
    try { cancelActiveSpeech("pause_conversation"); } catch { /* noop */ }
    stopSpeaking("pauseConversation");
    _disposeBargeIn();
    _releaseMic();
    setConversationActive(false);
    conversationActiveRef.current = false;
    setInactivityNotice(false);
    setStatus((s) => (s === "listening" ? "idle" : s));
    // B (PO 2026-08-07) · release any held Screen Wake Lock. Safe
    // no-op if none was ever acquired or if the browser already
    // released it via visibilitychange.
    try { pushSpeechDiag("wakeLock:release_context", { source: "pauseConversation" }); } catch { /* noop */ }
    try { releaseWakeLock("pauseConversation"); } catch { /* noop */ }
  }, [_clearInactivityTimer, _disposeBargeIn, _releaseMic]);

  const _resetInactivityTimer = useCallback(() => {
    _clearInactivityTimer();
    if (!conversationActiveRef.current) return;
    inactivityTimerRef.current = setTimeout(() => {
      // P0 RC-5 (PO 2026-08-09) · TRUE-IDLE guard. The 5-minute
      // ceiling exists to catch genuine abandonment. Any active
      // work state (Levi speaking, contractor speaking, command
      // in flight, Vision recording/paused/finalizing/uploading,
      // post-walk handoff pending) must suspend the shutdown and
      // re-arm for another full 5-minute window.
      const active = _leviIsActiveWork();
      if (active.active) {
        try {
          pushSpeechDiag("inactivityTimer:suspended", {
            reason: active.reason,
          });
        } catch { /* noop */ }
        inactivityTimerRef.current = setTimeout(() => {
          try {
            // Recursive fire-time re-check. If still active, keep
            // suspending; otherwise end the conversation cleanly.
            const stillActive = _leviIsActiveWork();
            if (stillActive.active) {
              _resetInactivityTimer();
              return;
            }
            endConversation({ reason: "inactivity" });
          } catch { /* noop */ }
        }, CONVERSATION_INACTIVITY_MS);
        inactivityArmedAtRef.current = Date.now();
        inactivityDeadlineRef.current = Date.now() + CONVERSATION_INACTIVITY_MS;
        return;
      }
      endConversation({ reason: "inactivity" });
    }, CONVERSATION_INACTIVITY_MS);
    inactivityArmedAtRef.current = Date.now();
    inactivityDeadlineRef.current = Date.now() + CONVERSATION_INACTIVITY_MS;
  }, [_clearInactivityTimer, endConversation]);

  // Phase 7-C · barge-in lifecycle. Dispose is idempotent and safe
  // to call repeatedly. Only armed while Levi speaks AND the loop
  // is active AND tab visible.
  const _armBargeIn = useCallback(({ onFire }) => {
    _disposeBargeIn();  // guarantee single detector
    if (!streamRef.current) return;
    if (!conversationActiveRef.current) return;
    if (typeof document !== "undefined" && document.hidden) return;
    bargeInRef.current = createBargeInDetector({
      stream: streamRef.current,
      // PO 2026-08-30 · P0 Voice · Correction B3. Inject the shared
      // diagnostic sink so createBargeInDetector emits bargeIn:*
      // events into the same ring as leviVAD:*. Keeps
      // leviBargeIn.js free of any cross-module import. ttsSource
      // is best-effort — the underlying pipeline may not always
      // know which path the current utterance took at arm time.
      pushDiag: pushSpeechDiag,
      ttsSource: (typeof window !== "undefined"
                  && window.__leviLastTtsSource) || null,
      onDetected: (info) => {
        // Detector auto-disposes after firing (fire-once). Clear
        // our ref so future dispose() calls remain no-ops.
        bargeInRef.current = null;
        try { onFire && onFire(info); } catch { /* noop */ }
      },
    });
  }, [_disposeBargeIn]);

  // ------------------------------------------------------------
  // LEVI-CONT-01 · session rehydration
  // ------------------------------------------------------------
  // On mount, hydrate the session id from localStorage. If that
  // fails (missing / expired / unknown to the server) fall back to
  // GET /api/levi/my-current-session for the caller's most recent
  // non-deleted session. Rehydration NEVER auto-executes any pending
  // action; it only restores useful context so the next turn stays
  // in the same conversation.
  const hydratedOnceRef = useRef(false);
  // Shared hydration step: given a session id known to belong to the
  // caller, fetch its turns and pending state and populate the local
  // hook state. Used by BOTH the localStorage path and the backend
  // fallback so the two behave identically. Returns true on success
  // (a session was fetched), false otherwise. Never auto-executes.
  const hydrateFromSessionId = useCallback(async (sid) => {
    if (!sid) return false;
    try {
      const r = await api.get(`/levi/command/sessions/${sid}`);
      const d = r?.data;
      if (!d || !d.session_id) return false;
      setSessionId(d.session_id);
      const restored = (d.turns || []).map(t => ({
        role: t.role, text: t.text, timestamp: t.timestamp,
      }));
      // Idempotent replace — even when the same session is
      // rehydrated twice, setTurns to the server's canonical list
      // does not create duplicates because we set (not append).
      if (restored.length) setTurns(restored);
      // Restore visible pending-confirmation state so the contractor
      // sees that something is waiting — but do NOT fire it. The
      // next turn (yes / no / new intent) still routes through the
      // same intercepts as before. Expired nonces will be refused
      // by the Foundation task-aggregate invariants at execute time.
      if (d.pending_action) {
        setPendingAction({
          nonce: d.pending_action.nonce,
          spoken: d.pending_action.spoken || "",
          extra: d.pending_action.extra || {},
        });
      }
      try {
        if (typeof window !== "undefined" && window.localStorage) {
          window.localStorage.setItem(SESSION_STORAGE_KEY, d.session_id);
        }
      } catch { /* noop */ }
      return true;
    } catch {
      // 404 / auth / offline — caller decides next step.
      return false;
    }
  }, []);
  useEffect(() => {
    if (hydratedOnceRef.current) return;
    hydratedOnceRef.current = true;
    let cancelled = false;
    const tryHydrate = async () => {
      let sid = null;
      try {
        sid = (typeof window !== "undefined" && window.localStorage)
          ? window.localStorage.getItem(SESSION_STORAGE_KEY)
          : null;
      } catch { /* private mode / disabled storage */ }
      if (sid) {
        const ok = await hydrateFromSessionId(sid);
        if (cancelled) return;
        if (ok) return;
      }
      // Fallback discovery — ask the server for the caller's most
      // recent non-deleted session, then hydrate it through the same
      // helper so both paths restore the transcript + pending state.
      //
      // PO 2026-08-13 · Fresh-Session QA Contract. When the current
      // page load supplied ``?leviFreshSession=1``, skip this
      // fallback so the prior session cannot be resurrected. The
      // localStorage branch above already read null (the fresh-session
      // module cleared ``jc.levi.session_id`` at module load), and
      // the next ``POST /api/levi/command`` will naturally mint a
      // fresh session via the existing ``_load_or_create(None, uid)``
      // → ``_new_session_doc(uid)`` backend path.
      if (wasFreshSessionRequested()) {
        return;
      }
      //
      // NOTE (StrictMode-safe): we intentionally do NOT gate the
      // hydrateFromSessionId call on `cancelled`. In dev with React
      // StrictMode double-mount, the first effect closure is
      // cancelled between the two awaits, but hydrateFromSessionId's
      // localStorage.setItem side-effect must still fire so the
      // subsequent mount can take the localStorage branch above and
      // restore the transcript. setSessionId/setTurns on an
      // already-unmounted component are silent no-ops in React 18.
      try {
        const r = await api.get("/levi/my-current-session");
        const nsid = r?.data?.session_id || null;
        if (nsid) {
          await hydrateFromSessionId(nsid);
        }
      } catch { /* unauthenticated / offline — no rehydration */ }
    };
    tryHydrate();
    return () => { cancelled = true; };
  }, [hydrateFromSessionId]);

  // Keep localStorage in sync with the current session id so a
  // refresh preserves the same conversation.
  useEffect(() => {
    if (typeof window === "undefined" || !window.localStorage) return;
    try {
      if (sessionId) window.localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    } catch { /* noop */ }
  }, [sessionId]);

  const submit = useCallback(async ({ text, file, activeProjectId, walkCompletedSessionId } = {}) => {
    setError("");
    setStatus("thinking");
    // Prime TTS INSIDE the user-gesture stack that led here (mic tap,
    // typed submit, confirm/cancel button). This unlocks iOS Safari's
    // audio session so the async speak() after the server reply is
    // honored. No-op after first invocation per tab.
    primeTts("submit");
    stopSpeaking("submit:before-fetch");
    // P0 REPAIR-3 (PO 2026-08-09) · submit-attribution diagnostic.
    // Records WHO called submit and the observable state at the
    // moment finalization happened. This is a read-only diagnostic
    // and does NOT alter Voice behavior. `lastStopReasonRef` is
    // stamped by the caller BEFORE invoking stopAndSend (VAD
    // endpoint, watchdog, empty-chunk retry, listen-watchdog
    // fallback). When submit() is invoked directly with `text` (typed)
    // or `walkCompletedSessionId` (post-walk), the reason is inferred.
    try {
      const _stopMeta = lastStopReasonRef.current || {};
      let inferredReason = _stopMeta.reason || null;
      let inferredSource = _stopMeta.source || null;
      if (!inferredReason) {
        if (walkCompletedSessionId) {
          inferredReason = "post_walk_handoff";
          inferredSource = "walk_replay";
        } else if (text != null && text !== "") {
          inferredReason = "typed";
          inferredSource = "ui_type";
        } else if (file) {
          inferredReason = "stop_and_send_audio";
          inferredSource = "stop_and_send";
        } else {
          inferredReason = "unknown";
          inferredSource = "unknown";
        }
      }
      const _rec = recRef.current;
      const _stream = streamRef.current;
      const _tracks = (_stream && _stream.getTracks && _stream.getTracks()) || [];
      const _vadDiag = (vadRef.current && typeof vadRef.current.getDiagnostics === "function")
        ? vadRef.current.getDiagnostics()
        : null;
      const _elapsedListeningMs = listenEpochStartedAtRef.current
        ? Math.max(0, Date.now() - listenEpochStartedAtRef.current)
        : null;
      pushSpeechDiag("voice_submit:requested", {
        submit_reason: inferredReason,
        submit_source: inferredSource,
        stop_meta_at: _stopMeta.at || null,
        listening_epoch: listenEpochRef.current || 0,
        elapsed_listening_ms: _elapsedListeningMs,
        voice_ever: !!listenEpochVoiceEverRef.current,
        contractor_speaking: !!contractorSpeakingRef.current,
        recorder_state: (_rec && _rec.state) || null,
        mic_tracks: _tracks.map((t) => ({
          kind: t.kind, readyState: t.readyState, muted: !!t.muted, enabled: !!t.enabled,
        })),
        vad: _vadDiag,
        conversation_status: statusRef.current || null,
        has_walk_completed_session_id: !!walkCompletedSessionId,
        has_typed_text: !!(text != null && text !== ""),
        has_audio_file: !!file,
      });
      // Consume the stamped reason so a follow-up submit that lacks
      // an explicit stamp does not re-use this one.
      lastStopReasonRef.current = { reason: null, source: null, at: null };
    } catch { /* diagnostic must never throw */ }
    try {
      const fd = new FormData();
      if (sessionId) fd.append("session_id", sessionId);
      fd.append("ui_language", lang || "en");
      // PO 2026-08-13 Boundary B · read pathname from the stable
      // ``pathnameRef`` mirror instead of the closure-captured
      // ``location`` so this useCallback's identity does not churn
      // on route navigation. Behavior is unchanged — the ref is
      // updated by the tiny mirror effect above whenever the router
      // path changes.
      const apid = activeProjectId || readActiveProjectId(pathnameRef.current);
      if (apid) fd.append("active_project_id", apid);
      const ajid = readActiveJobId(pathnameRef.current);
      if (ajid) fd.append("active_job_id", ajid);
      // Slice LEVI-NAV-01 · sanitized workspace_context. The
      // leviWorkspace snapshot is already allowlist-scrubbed on the
      // client; the backend re-sanitizes on receipt.
      try {
        const wsSnap = readLeviWorkspaceSnapshot({
          route_classification: (pathnameRef.current || "").split("/").slice(0, 3).join("/"),
          active_project_id: apid || undefined,
        });
        if (wsSnap && Object.keys(wsSnap).length > 0) {
          fd.append("workspace_context", JSON.stringify(wsSnap));
        }
      } catch (_) { /* never let workspace snapshot block a submit */ }
      if (file) fd.append("file", file);
      else if (text) fd.append("text", text);
      // Post-walk continuity handoff (PO 2026-08-08). When the recorder
      // emits levi:vision-walk-stopped with finalized=true we call
      // submit({ walkCompletedSessionId }) so the drawer's conversation
      // renders Levi's automatic post-walk reply exactly like any /command
      // turn. Backend detects walk_completed_session_id and returns the
      // walk-completed envelope directly.
      if (walkCompletedSessionId) {
        fd.append("walk_completed_session_id", walkCompletedSessionId);
        if (!text && !file) {
          // Backend requires SOME turn input; a marker keeps it valid.
          fd.append("text", "");
        }
      }
      // Checkpoint 2 · attach active Video Walk session id so the
      // server can bind it to the vision_objective (used by
      // vision_finalize_request to route back).
      if (activeVisionSessionIdRef.current) {
        fd.append("video_walk_session_id",
                  activeVisionSessionIdRef.current);
      }
      // Vision Conversational Deployment · C2 · current-frame snap.
      // When the live-conversation feature is ON AND a Vision walk is
      // actively recording AND this turn is a contractor utterance
      // (not a post-walk handoff / not already carrying an audio
      // file), capture a still frame from the active preview and
      // attach it as ``image``. The backend adapter — gated by the
      // same server-side flag AND presence of a pending
      // vision_objective — routes to run_recommendation() with the
      // frame + utterance. Absence of the field, absence of vision
      // objective, or flag OFF preserves the exact existing routing.
      try {
        if (isVisionLiveConversationEnabled()
            && !walkCompletedSessionId
            && activeVisionSessionIdRef.current
            && (typeof window !== "undefined")
            && window.__leviVisionState
            && window.__leviVisionState.active) {
          const _snap = await captureVisionSnapshot();
          if (_snap && _snap.size > 0) {
            const _imgFile = new File([_snap],
              `vision-frame-${Date.now()}.jpg`,
              { type: _snap.type || "image/jpeg" });
            fd.append("image", _imgFile);
          }
        }
      } catch { /* snapshot failure must never block a submit */ }
      const r = await api.post("/levi/command", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      const d = r.data;
      setSessionId(d.session_id);
      setTurns(d.turns || []);
      setPendingAction(d.pending_action || null);
      setLastVisible(d.visible_reply || d.spoken_reply || "");
      setLastMatches(d.matches || null);
      setLastExtra(d);
      // P0 (2026-08-04 round 3) · Race fix. React state → ref
      // propagation runs via the useEffect at the top of the hook
      // (`useEffect(() => { lastExtraRef.current = lastExtra; })`)
      // which fires AFTER commit. On a fast TTS path (short reply,
      // barge-in during TTS, or immediate onEnd) the closure's
      // _onSpokenEnd / _onBargeIn could read the STALE previous
      // extra from the ref and miss the CURRENT reply's
      // ``conversation_control === "end"`` — allowing the mic to
      // re-arm. Update the ref synchronously here so both the
      // closure (which reads `d` first) AND any callback that
      // reads `lastExtraRef.current` see the same authoritative
      // envelope.
      try { lastExtraRef.current = d; } catch { /* noop */ }
      // Route the reply text through the SHARED speakLeviReply entry
      // point — same pipeline as the diagnostic "Test Levi Voice"
      // button.
      const replyText = d.spoken_reply || d.visible_reply || "";
      const replyLocale = d.language || lang || "en";
      // Phase 7-B · shared onEnd handler. Arms auto-listen when the
      // reply expects a contractor turn AND the session is
      // actively-driven by an explicit mic tap. Never fires on
      // conversation_control=end, ready_for_review, paused, or
      // archived layers.
      const _onSpokenEnd = (info = {}) => {
        const _reason = info?.reason || "unknown";
        // Instrumented diagnostics per PO request 2026-02.
        const _snapshot = {
          reason: _reason,
          conversationActive: conversationActiveRef.current,
          expects_reply: (lastExtraRef.current || d || {}).expects_reply,
          expected_answer_type: (lastExtraRef.current || d || {}).expected_answer_type,
          conversation_control: (lastExtraRef.current || d || {}).conversation_control,
          action_type: (lastExtraRef.current || d || {}).action_type,
          conversation_layer: (lastExtraRef.current || d || {}).conversation_layer,
          permission: permissionRef.current,
          tab_hidden: typeof document !== "undefined" ? !!document.hidden : null,
          status: statusRef.current,
          mediaRecorder_state: recRef.current
            ? recRef.current.state : null,
          stream_active: !!(streamRef.current
                             && streamRef.current.active),
          inactivity_timer_before: !!inactivityTimerRef.current,
        };
        try { console.log("[7C onSpokenEnd]", _snapshot); }
        catch { /* noop */ }
        // Phase 7-C · dispose barge-in when TTS ends (any reason).
        _disposeBargeIn();
        // ------------------------------------------------------------
        // QA Round 3 · lifecycle consistency fix.
        // The inactivity timer must ONLY be armed when we are
        // actually going to keep listening (mic auto-re-arm path).
        // If ANY skip condition applies, cancel the timer so the UI
        // state (idle / Tap to speak) and the timer state agree.
        // Without this, a Package 8-A reply that skipped auto-listen
        // still fired an inactivity closeout 30s later because the
        // timer had been armed unconditionally.
        // ------------------------------------------------------------
        const _skipAndSilence = (reasonTag) => {
          try { console.log(`[7C onSpokenEnd] SKIP ${reasonTag}`); } catch { /* noop */ }
          try { _clearInactivityTimer(); } catch { /* noop */ }
        };
        if (!conversationActiveRef.current) {
          _skipAndSilence("conversationActive_false");
          return;
        }
        // P0 (2026-08-04) · Explicit-end read fix.
        // Prior code: ``const ex = lastExtraRef.current || d || {};``
        // The `||` chain returns ``lastExtraRef.current`` whenever it
        // is truthy — which, after any prior conversation turn, it
        // ALWAYS is. That masked the CURRENT response's
        // ``conversation_control === "end"`` behind the previous
        // turn's stale extra, because ``setLastExtra`` had not yet
        // propagated to the ref by the time the farewell TTS
        // finished. The closure-captured ``d`` IS this exact reply,
        // so we prefer it. Both are OR'd for the end check so the
        // authoritative end signal ALWAYS wins whichever source
        // updated first.
        const ex = d || lastExtraRef.current || {};
        const _isExplicitEnd = (
          (d && d.conversation_control === "end")
          || ((lastExtraRef.current || {}).conversation_control === "end")
        );
        // Recorder-lifecycle defect fix (PO 2026-08-04 round 2 + round 3).
        // A farewell reply carries ``conversation_control === "end"``.
        // Its terminal TTS must ALWAYS release the mic — even if the
        // reason we got here is a barge-in (residual voice bleed
        // during Levi's goodbye) or any other non-"ended" reason.
        // The end signal ALWAYS wins.
        if (_isExplicitEnd) {
          _skipAndSilence("conversation_control_end");
          try { endConversation({ reason: "voice" }); } catch { /* noop */ }
          return;
        }
        // Barge-in path — the barge-in fire handler ALREADY calls
        // startListening immediately. Do not double-arm here.
        if (_reason === "barge_in") {
          try { console.log("[7C onSpokenEnd] SKIP barge_in (arm handled by barge-in path)"); } catch { /* noop */ }
          // Barge-in re-armed the mic already; reset the ceiling
          // normally so the loop keeps its 30s window.
          try { _resetInactivityTimer(); } catch { /* noop */ }
          return;
        }
        // Continuous Conversation contract (PO 2026-08-02 correction).
        // ``expects_reply`` is metadata about the reply's *content* —
        // NOT a loop-control signal. In an active Coworker
        // conversation we keep listening until the contractor ends
        // (Stop / drawer close / tab hide / permission revoke), Levi
        // naturally concludes (``conversation_control === "end"`` —
        // already handled above), or the idle ceiling expires. A
        // generic acknowledgment (``expects_reply=false``) must NOT
        // close the loop.
        if (typeof document !== "undefined" && document.hidden) {
          _skipAndSilence("tab_hidden");
          return;
        }
        if (statusRef.current === "listening") {
          try { console.log("[7C onSpokenEnd] SKIP already_listening"); } catch { /* noop */ }
          // Already listening — the loop is alive, keep the ceiling.
          try { _resetInactivityTimer(); } catch { /* noop */ }
          return;
        }
        if (permissionRef.current === "denied") {
          _skipAndSilence("permission_denied");
          return;
        }
        try { console.log("[7C onSpokenEnd] ARM startListening in 200ms"); } catch { /* noop */ }
        // Arm the inactivity ceiling only now, when we are actually
        // going to keep listening. The timer + the mic + presence
        // state now all agree.
        try { _resetInactivityTimer(); } catch { /* noop */ }
        // PO 2026-08-13 Boundary A · capture setTimeout id so the
        // unmount cleanup below can clear it. Prevents an outgoing
        // hook instance from re-arming the mic post-unmount.
        try { if (phase7cArmTimerRef.current) clearTimeout(phase7cArmTimerRef.current); } catch { /* noop */ }
        phase7cArmTimerRef.current = setTimeout(() => {
          phase7cArmTimerRef.current = null;
          try {
            if (conversationActiveRef.current
                && !document.hidden
                && statusRef.current !== "listening"
                && startListeningRef.current) {
              startListeningRef.current();
            } else {
              // Something changed between deciding to arm and the
              // 200ms delay firing (tab hidden, already listening,
              // conversation ended). Cancel the ceiling so we do
              // not fire a closeout against an idle UI.
              try { _clearInactivityTimer(); } catch { /* noop */ }
            }
          } catch (e) {
            try { console.log("[7C onSpokenEnd] setTimeout threw", String(e)); } catch { /* noop */ }
            try { _clearInactivityTimer(); } catch { /* noop */ }
          }
        }, 200);
      };
      // Phase 7-C · barge-in detector — arm as soon as we know we
      // are about to speak. It stays armed until TTS onEnd fires
      // (any reason), at which point we dispose it above.
      const _onBargeIn = (_info) => {
        try { console.log("[7C bargeIn] detected — cancelling TTS + arming mic"); } catch { /* noop */ }
        try { cancelActiveSpeech("barge_in"); } catch { /* noop */ }
        // Recorder-lifecycle defect fix (PO 2026-08-04 rounds 2 + 3).
        // If the current reply is a farewell
        // (``conversation_control === "end"``), a barge-in during the
        // goodbye TTS is almost always residual voice bleed, NOT the
        // contractor wanting to continue. Do NOT re-arm the mic —
        // end the conversation instead, so the recorder returns to
        // idle consistently regardless of how the TTS terminated.
        //
        // Round 3: prefer the closure-captured ``d`` (this exact
        // response) FIRST, then fall back to ``lastExtraRef.current``.
        // Prior code read the ref first, which — being any prior
        // truthy extra — masked the CURRENT reply's end signal.
        const _isExplicitEnd = (
          (d && d.conversation_control === "end")
          || ((lastExtraRef.current || {}).conversation_control === "end")
        );
        if (_isExplicitEnd) {
          try { console.log("[7C bargeIn] farewell in flight — ending instead of re-arming"); } catch { /* noop */ }
          try { endConversation({ reason: "voice" }); } catch { /* noop */ }
          return;
        }
        try {
          if (conversationActiveRef.current
              && !document.hidden
              && statusRef.current !== "listening"
              && startListeningRef.current) {
            startListeningRef.current();
          }
        } catch { /* noop */ }
      };
      // PO 2026-08-30 · P0 Voice · D1 (late-arm barge-in). The
      // pre-emptive `_armBargeIn` at this line has been REMOVED.
      // Barge-in is now armed only when the actual TTS output path
      // is known and provably referenceable by Chromium's WebRTC
      // AEC — i.e. inside the server-TTS `onPlaybackStart` callback
      // wired below. The browser-speechSynthesis fallback route
      // (503 / timeout / decode error) intentionally leaves the
      // barge-in detector un-armed for the duration of that
      // utterance, preventing catastrophic self-interruption from
      // OS-external speech output that bypasses the AEC reference.
      // Hands-free post-TTS re-arm remains unchanged: `_onSpokenEnd`
      // arms `startListening` via the 200 ms setTimeout regardless
      // of which output path just finished.
      // Phase 7-D · when the server envelope carries a voice_id, try
      // server-side OpenAI TTS first; fall back to speechSynthesis on
      // any failure inside playServerTtsForReply.
      //
      // Recorder-lifecycle report 2026-08-04 · surface an HONEST
      // inline notice when the server route falls back to browser
      // speechSynthesis (503, timeout, decode error). Never implies
      // the contractor's device is at fault.
      const _onServerTtsFallback = (info) => {
        try { console.log("[7C serverTts] fallback", info?.reason || ""); } catch { /* noop */ }
        // PO 2026-08-30 · P0 Voice · D1 diagnostic hint. Mark the
        // active TTS source for any diagnostic emit that may still
        // reference `window.__leviLastTtsSource` (B3 correlation).
        try {
          if (typeof window !== "undefined") window.__leviLastTtsSource = "browser_tts_fallback";
        } catch { /* noop */ }
        const notice = (lang === "es")
          ? "No pude reproducir mi respuesta en voz alta esta vez, pero la encontrarás en la conversación de arriba."
          : "I wasn't able to play my response out loud this time, but you'll find it in our conversation above.";
        try { setError(notice); } catch { /* noop */ }
      };
      // PO 2026-08-30 · P0 Voice · D1 arm-on-playback callback. Fires
      // exactly once when the SERVER-TTS <audio> element actually
      // begins playing — the moment Chromium's AEC has a reference.
      // Does not fire on the browser-speechSynthesis fallback path.
      const _onServerTtsPlaybackStart = () => {
        try { console.log("[7C serverTts] playback-start · arming barge-in"); } catch { /* noop */ }
        try {
          if (typeof window !== "undefined") window.__leviLastTtsSource = "server_tts";
        } catch { /* noop */ }
        try { _armBargeIn({ onFire: _onBargeIn }); } catch { /* noop */ }
      };
      // P0 RC-5-follow-up · commit to the Levi turn BEFORE any
      // server-TTS request goes out so the inactivity guard treats
      // this response as active work throughout the 503 → local-
      // fallback race window. The `_onSpokenEnd` handler is
      // wrapped below to release the flag when the turn actually
      // finishes (or when a legitimate cancellation clears it).
      _beginLeviTurn(d.voice_id_for_playback ? "server_tts" : "browser_tts");
      const _endTurnOnce = (reason) => {
        try { _endLeviTurn(reason || "spoken_end", "completion"); }
        catch { /* noop */ }
      };
      const _onSpokenEndWrapped = (info) => {
        _endTurnOnce((info && info.reason) || "spoken_end");
        try { _onSpokenEnd(info); } catch { /* noop */ }
      };
      if (d.voice_id_for_playback) {
        playServerTtsForReply(replyText, replyLocale, {
          voice_id: d.voice_id_for_playback,
          style: d.communication_style || undefined,
          speed: d.playback_speed || undefined,
          readAloud: d.read_aloud_enabled,
          source: "normal_reply", mode: "command", status: "thinking",
          onEnd: _onSpokenEndWrapped,
          onFallback: _onServerTtsFallback,
          // PO 2026-08-30 · P0 Voice · D1. Barge-in arms only when
          // the server-TTS <audio> starts playing (AEC-reference
          // path). Browser-speechSynthesis fallback deliberately
          // skips this callback.
          onPlaybackStart: _onServerTtsPlaybackStart,
        });
      } else {
        speakLeviReply(replyText, replyLocale, {
          source: "normal_reply", mode: "command", status: "thinking",
          onEnd: _onSpokenEndWrapped,
        });
      }
      // P0 RCA 2026-08-09 · DO NOT arm the inactivity ceiling here.
      // Previously this line armed the 30-second ceiling BEFORE Levi's
      // TTS started playing — burning 10-20 seconds of the ceiling on
      // Levi's own reply and leaving little headroom for the
      // contractor to respond, especially on long post-walk summaries.
      // The ceiling is now armed exclusively in ``_onSpokenEnd`` when
      // we know Levi has finished speaking AND the mic is being re-
      // armed for the contractor. The `onVoiceStart` VAD callback
      // then resets it once the contractor's sustained voice is
      // detected.
      // (Formerly: try { _resetInactivityTimer(); } catch { /* noop */ })
      // Continuous Conversation contract (PO 2026-08-02 correction).
      // The end action is now deferred to ``_onSpokenEnd`` so the
      // closing TTS ("Sounds good. I'm here whenever you need me.")
      // is allowed to play in full before the loop tears down. The
      // ``_onSpokenEnd`` handler observes ``conversation_control ===
      // "end"`` and calls ``endConversation`` there.
      if (d.action_type === "awaiting_confirmation") setStatus("awaiting_confirmation");
      else if (d.action_type === "sent") setStatus("sent");
      else if (d.action_type === "opened") setStatus("opened");
      else if (d.action_type === "navigate") setStatus("opened");
      else if (d.action_type === "open_camera") setStatus("opened");
      else if (d.action_type === "unsupported") setStatus("unclear");
      else setStatus("idle");
      // Checkpoint 2 (2026-08-06) — dispatch vision-lifecycle
      // action envelopes to the recorder via window events so the
      // existing ``VideoWalkRecorder`` handles state transitions
      // without a new UI surface. All events are additive.
      try {
        if (d.action_type === "vision_finalize_request"
            || d.action_type === "vision_cancel_request"
            || d.action_type === "vision_pause_request"
            || d.action_type === "vision_resume_request") {
          window.dispatchEvent(new CustomEvent(
            `levi:${d.action_type.replace(/_/g, "-")}`,
            { detail: d }));
        }
      } catch (e) {
        console.warn("[LEVI-VISION-ACTION] dispatch failed:", e);
      }
      // Vision Conversational Deployment · Checkpoint 1 fix
      // (PO 2026-08-05): retarget the ``open_camera`` navigation to
      // ProjectDetail with a ``levi_vision=1`` query flag. This
      // reuses the existing VideoWalkHistory recorder mount instead
      // of routing through a non-existent NewEstimate sub-URL. The
      // recorder is REVEALED, not auto-started (D-2 policy).
      // Flag-gated. When OFF, the legacy (broken) URL is kept so no
      // navigation behavior changes for users still on the previous
      // recognizer path.
      if (d.action_type === "open_camera") {
        try {
          const pid = d.active_project_id
                    || (d.extra && d.extra.active_project_id)
                    || d.project_id
                    || null;
          const surface = d.extra?.surface || d.surface || "video_walk_recorder";
          // Vision Three-Stitch (c) · Session-scoped Vision routing.
          // When conversational Vision intent resolves WITHOUT a
          // project (unscheduled field arrival), the backend creates
          // or reuses a levi_estimate_sessions doc and returns
          //   surface = "video_walk_recorder_session_scoped"
          //   levi_estimate_session_id = <lsid>
          // Route to the Custom Job page with an explicit camera flag
          // + the pre-created session id so LeviEstimate reuses that
          // session (no duplicate) and auto-opens the recorder.
          const lsid = d.extra?.levi_estimate_session_id
                    || d.levi_estimate_session_id
                    || null;
          if (surface === "video_walk_recorder_session_scoped" && lsid) {
            navigate(`/app/estimates/new/levi?levi_camera=1&lsid=${encodeURIComponent(lsid)}`);
            return;
          }
          const vcEnabled = String(
            process.env.REACT_APP_LEVI_VISION_CONVERSATIONAL_ENABLED || ""
          ).toLowerCase() === "1"
            || String(
              process.env.REACT_APP_LEVI_VISION_CONVERSATIONAL_ENABLED || ""
            ).toLowerCase() === "true";
          if (pid && surface === "video_walk_recorder" && vcEnabled) {
            navigate(`/app/projects/${pid}?levi_vision=1`);
          } else if (pid && surface === "video_walk_recorder") {
            navigate(`/app/projects/${pid}/estimates/new?levi_camera=1`);
          } else if (surface === "video_walk_recorder") {
            navigate(`/app/estimates/new?levi_camera=1`);
          }
        } catch (e) {
          console.warn("[LEVI-OPEN-CAMERA] navigation failed:", e);
        }
      }
      // Slice LEVI-NAV-01 · automatic navigation on structured
      // navigation payload. Falls back to no-op if performNavigation
      // rejects (unknown destination_key, low confidence, or
      // dirty-state cancel). Never mutates domain records.
      if (d && d.navigation && d.action_type === "navigate") {
        try {
          await performNavigation(d.navigation, {
            lang: d.language || lang || "en",
            sessionId: d.session_id,
          });
        } catch (e) {
          console.warn("[LEVI-NAV-01] performNavigation failed:", e);
        }
      }
      // Fire the customer-send hook if the server told us to. The route
      // is emitted only by the server and must match the fixed
      // /api/email/{proposal,contract,closeout}/{id} allowlist — a
      // defensive client-side check that rejects any surprise route.
      if (d.execute_route && d.http_method === "POST") {
        const ALLOW = /^\/api\/email\/(proposal|contract|closeout)\/[A-Za-z0-9-]+$/;
        if (!ALLOW.test(d.execute_route)) {
          setError(lang === "es" ? "Ruta de envío no permitida." : "Send route not allowed.");
        } else {
          try {
            await api.post(d.execute_route);
          } catch {
            setError(lang === "es" ? "No se pudo entregar al cliente." : "Could not deliver to the customer.");
          }
        }
      }
      return d;
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Command failed";
      setError(msg);
      setStatus("error");
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, sessionId, performNavigation,
      _resetInactivityTimer, endConversation]);
  // PO 2026-08-13 Boundary B · ``location.pathname`` intentionally
  // omitted from ``submit``'s dep array; the closure now reads
  // ``pathnameRef.current`` which is kept fresh by the mirror effect
  // at the top of the hook. This keeps ``submit``'s identity stable
  // across route navigations and prevents the post-walk useEffect
  // below from re-running (and re-scheduling untracked setTimeouts)
  // on every page change. Observable behavior of any individual
  // turn is unchanged.

  // P0 stabilization 2026-08-09 · wake-lock aligned with the WHOLE
  // conversation, not just mic-listening. When any code path sets
  // conversationActive=true (typed submit, mic tap, buffered post-
  // walk handoff), request the screen wake lock. iOS Safari may
  // auto-release on hidden — the visibility handler above re-
  // acquires on visible return. On conversationActive→false the
  // endConversation path already releases; this effect is
  // idempotent and never double-releases.
  useEffect(() => {
    if (!conversationActive) return undefined;
    try {
      pushSpeechDiag("wakeLock:conversation_start_request", {});
      acquireWakeLock("conversation_active").catch(() => {});
    } catch { /* noop */ }
    return undefined;
  }, [conversationActive]);

  const startListening = useCallback(async () => {
    // Phase 7-B · explicit contractor activation. Opening the drawer
    // alone does NOT set this flag; only a mic tap does.
    setConversationActive(true);
    setInactivityNotice(false);
    // Prime TTS synchronously in the tap handler BEFORE any await so
    // iOS Safari accepts the priming utterance within the gesture.
    primeTts("startListening");
    stopSpeaking("startListening");
    setError("");
    let stream;
    try {
      // Vision Conversational Deployment · C1 · shared-stream adopt.
      // When Vision is actively recording and the feature flag is
      // ON, adopt the Vision-owned MediaStream instead of opening a
      // second concurrent getUserMedia (iOS Safari does not honor
      // that reliably). Ownership is marked so _releaseMic never
      // stops the underlying tracks — Vision remains the sole
      // authority on final teardown. Falls through to the normal
      // path when Vision is inactive, the shared stream is
      // unavailable, or the flag is OFF.
      const _visionStream = (typeof window !== "undefined")
        ? window.__leviVisionMediaStream : null;
      const _visionActive = (typeof window !== "undefined"
                             && window.__leviVisionState
                             && window.__leviVisionState.active) === true;
      let _adoptedVision = false;
      if (isVisionLiveConversationEnabled()
          && _visionActive
          && _visionStream
          && _visionStream.getTracks
          && _visionStream.getTracks().some(
               (t) => t.kind === "audio" && t.readyState === "live")) {
        stream = _visionStream;
        streamRef.current = stream;
        visionOwnedStreamRef.current = true;
        _adoptedVision = true;
        try { console.log("[C1 vision-adopt] adopted Vision-owned MediaStream"); } catch { /* noop */ }
      }
      // P-2 (PO 2026-08-06) · Reuse the authorized MediaStream when
      // it is still healthy. iOS Safari denies getUserMedia outside a
      // fresh user gesture; reusing the existing stream between
      // Phase-7 auto-arm cycles keeps the conversation continuous
      // without introducing background listening. Final teardown
      // (drawer close, endConversation, permission revoke, Vision
      // claim, tab hidden) will have already fully released the
      // stream via _releaseMic({}), so this branch cannot resurrect
      // a stream after an intentional stop.
      if (!_adoptedVision && _hasHealthyStream()) {
        stream = streamRef.current;
        try { console.log("[P-2 stream-reuse] reusing authorized MediaStream"); } catch { /* noop */ }
      } else if (!_adoptedVision) {
        // PO 2026-08-30 · P0 Voice · Correction B1 (bounded).
        // Explicit AEC / noise-suppression / AGC constraints so the
        // browser actively cancels Levi's own TTS audio out of the
        // mic input when the contractor is on device speakers (not
        // headphones). Root cause of the false self-barge-in captured
        // on the repaired preview: Levi's local speechSynthesis
        // fallback bled back into the reused mic at RMS ≥ 0.06 for
        // ≥ 250 ms and the sibling barge-in detector treated the
        // bleed as legitimate voice. Universally supported by
        // Chromium, Edge, Firefox, and Safari 14+. Does NOT create a
        // second stream, second owner, second VAD, or change Vision
        // ownership.
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
        streamRef.current = stream;
      }
      permissionRef.current = "granted";
    } catch {
      permissionRef.current = "denied";
      setConversationActive(false);
      setError(lang === "es" ? "No se otorgó el micrófono. Puedes escribir tu mensaje."
                             : "Microphone permission denied. You can type your message.");
      return;
    }
    const mime = pickMime();
    let rec;
    try {
      rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    } catch {
      setError(lang === "es" ? "Este navegador no admite grabación de voz."
                             : "This browser does not support voice recording.");
      return;
    }
    chunksRef.current = [];
    rec.ondataavailable = (ev) => { if (ev.data?.size) chunksRef.current.push(ev.data); };
    rec.start(CHUNK_MS);
    recRef.current = rec;
    setStatus("listening");
    stopInFlightRef.current = false;

    // B (PO 2026-08-07) · Foreground Screen Wake Lock. Attach to the
    // existing active-conversation lifecycle. Fire-and-forget; the
    // helper is safe on unsupported browsers, on rejection, and on
    // duplicate calls. Release triggers are wired at every existing
    // ending path (endConversation, pauseConversation,
    // _releaseMic full-teardown, visibilitychange to hidden, unmount).
    try { acquireWakeLock("startListening"); } catch { /* noop */ }

    // Recorder-lifecycle hard ceiling (PO 2026-08-04). Force-close
    // the mic if this listening session ever runs past
    // ``LISTEN_HARD_CEILING_MS`` without endpointing. Cleared by
    // any successful stopAndSend / _releaseMic path.
    _clearListenWatchdog();
    listenWatchdogRef.current = setTimeout(() => {
      try { console.log("[recorder] hard-ceiling reached — force-releasing mic"); } catch { /* noop */ }
      const cue = lang === "es"
        ? "Cerré el micrófono por seguridad. Toca cuando quieras seguir."
        : "I closed the mic to be safe — tap when you're ready to keep going.";
      try { setError(cue); } catch { /* noop */ }
      // P0 REPAIR-3 · stamp attribution so submit() can identify this
      // last-line safety trigger. Behavior unchanged.
      try {
        lastStopReasonRef.current = {
          reason: "watchdog_hard_ceiling",
          source: "listen_watchdog",
          at: Date.now(),
        };
      } catch { /* noop */ }
      // Force-close through stopAndSend so any partial capture still
      // reaches the server; if there are no chunks the empty-chunk
      // branch already handles the yield. On empty capture the
      // reduced MAX_EMPTY_CHUNK_RETRIES=1 keeps the fallback quiet.
      try { stopAndSendRef.current && stopAndSendRef.current(); }
      catch {
        // Last resort — release directly and drop to idle.
        try { _releaseMic(); } catch { /* noop */ }
        try { setStatus("idle"); } catch { /* noop */ }
      }
    }, LISTEN_HARD_CEILING_MS);

    // LEVI-CONT-01 · start the additive VAD. Fail-open: if the
    // module can't attach (no AudioContext, no permission), the
    // returned object is a no-op and manual Stop remains
    // authoritative.
    try { vadRef.current && vadRef.current.dispose(); } catch { /* noop */ }
    // P0 RC-6 (PO 2026-08-09) · advance the listening epoch. VAD
    // callbacks below capture `myEpoch` in closure and guard against
    // stale firings from a prior listening session.
    listenEpochRef.current = (listenEpochRef.current || 0) + 1;
    const myEpoch = listenEpochRef.current;
    listenEpochStartedAtRef.current = Date.now();
    listenEpochStoppedAtRef.current = null;
    listenEpochStopReasonRef.current = null;
    listenEpochVoiceEverRef.current = false;
    try { pushSpeechDiag("listening:epoch_start", { epoch: myEpoch }); } catch { /* noop */ }

    vadRef.current = createLeviVAD({
      stream,
      // PO 2026-08-29 · P0 Voice bounded repair. Inject the existing
      // shared diagnostic sink so leviVAD.js can emit vad:audioctx_*
      // and vad:rms_snapshot events into the same ring the rest of
      // the voice pipeline uses. Keeps leviVAD.js free of any
      // cross-module import.
      pushDiag: pushSpeechDiag,
      isSpeaking: () => {
        try {
          return !!(typeof window !== "undefined"
                    && window.speechSynthesis
                    && (window.speechSynthesis.speaking
                        || window.speechSynthesis.pending));
        } catch { return false; }
      },
      onVoiceStart: () => {
        // P0 RC-6 · stale-epoch guard.
        if (myEpoch !== listenEpochRef.current) {
          listenEpochStaleIgnoredRef.current += 1;
          try { pushSpeechDiag("listening:stale_ignored", { event: "voice_start", epoch: myEpoch, current: listenEpochRef.current }); } catch { /* noop */ }
          return;
        }
        try { contractorSpeakingRef.current = true; } catch { /* noop */ }
        listenEpochVoiceEverRef.current = true;
        try {
          lastInactivityResetReasonRef.current = "contractor_voice_start";
          _resetInactivityTimer();
        } catch { /* noop */ }
        try { pushSpeechDiag("contractorVoice:start", { epoch: myEpoch }); } catch { /* noop */ }
      },
      onEndpoint: ({ reason, voiceEver }) => {
        // P0 RC-6 · stale-epoch guard. A VAD endpoint from a prior
        // listening session MUST NOT act — it would otherwise call
        // stopAndSend, submit an empty turn, and cancel a later
        // Levi turn (the exact PO-observed regression).
        if (myEpoch !== listenEpochRef.current) {
          listenEpochStaleIgnoredRef.current += 1;
          try { pushSpeechDiag("listening:stale_ignored", { event: "endpoint", epoch: myEpoch, current: listenEpochRef.current, reason, voiceEver: !!voiceEver }); } catch { /* noop */ }
          return;
        }
        listenEpochStoppedAtRef.current = Date.now();
        listenEpochStopReasonRef.current = reason || "unknown";
        try { contractorSpeakingRef.current = false; } catch { /* noop */ }
        try { pushSpeechDiag("contractorVoice:end", { reason, voiceEver: !!voiceEver, epoch: myEpoch }); } catch { /* noop */ }
        // P0 RC-6 · NO VOICE IS NOT A CONTRACTOR TURN. If the
        // contractor never spoke, DO NOT call stopAndSend — that
        // path submits an empty transcript, may cancel an active
        // Levi turn, or trigger a stale "Can you say that again?"
        // cue. Additionally, if a Levi turn is currently in
        // progress, no-voice endpoint must NOT touch anything.
        if (!voiceEver) {
          if (leviTurnInProgressRef.current) {
            try { pushSpeechDiag("listening:no_voice_deferred_levi_turn_active", { epoch: myEpoch }); } catch { /* noop */ }
          }
          // Release the mic quietly. Do NOT submit. Do NOT cancel
          // Levi. Do NOT end conversation.
          try { _releaseMic(); } catch { /* noop */ }
          // PO 2026-02 · Gabe Physical QA Failure-A repair.
          // "UI listening + resources torn down" state consistency:
          // when the conversation is still active, mirror the same
          // graceful re-arm the empty-chunks branch already uses.
          // Set status truthfully to "idle" and schedule
          // startListening on a short delay so the continuous
          // conversation lifecycle recovers on its own. Guards
          // (visibility, permission, active-conversation, Levi-turn-
          // in-progress, epoch-still-current) mirror the empty-
          // chunks branch verbatim so no new voice state machine
          // is introduced.
          try {
            if (conversationActiveRef.current
                && !leviTurnInProgressRef.current
                && (typeof document === "undefined" || !document.hidden)
                && permissionRef.current !== "denied") {
              try { setStatus("idle"); } catch { /* noop */ }
              try { pushSpeechDiag("listening:no_voice_auto_rearm_scheduled", { epoch: myEpoch }); } catch { /* noop */ }
              const _rearmEpoch = myEpoch;
              setTimeout(() => {
                try {
                  if (conversationActiveRef.current
                      && !leviTurnInProgressRef.current
                      && (typeof document === "undefined" || !document.hidden)
                      && permissionRef.current !== "denied"
                      && statusRef.current !== "listening"
                      && startListeningRef.current) {
                    try { pushSpeechDiag("listening:no_voice_auto_rearm_fire", { prior_epoch: _rearmEpoch }); } catch { /* noop */ }
                    startListeningRef.current();
                  }
                } catch { /* noop */ }
              }, 250);
            } else {
              // Not active anymore — set status idle so the visual
              // "listening" state does not linger while resources
              // are gone.
              try { setStatus((s) => (s === "listening" ? "idle" : s)); } catch { /* noop */ }
            }
          } catch { /* noop */ }
          return;
        }
        // Voice ended + silence hang → auto-submit through the
        // existing path exactly once.
        try {
          lastStopReasonRef.current = {
            reason: `vad_${reason || "endpoint"}`,
            source: "vad",
            at: Date.now(),
            listening_epoch: myEpoch,
          };
        } catch { /* noop */ }
        try { stopAndSendRef.current && stopAndSendRef.current(); }
        catch { /* noop */ }
      },
    });
  }, [lang, _clearListenWatchdog, _releaseMic, _hasHealthyStream]);

  const stopAndSend = useCallback(async () => {
    const rec = recRef.current;
    if (!rec) return;
    // LEVI-CONT-01 · idempotent stop. Both manual Stop and the VAD
    // endpoint can arrive; the recorder must only be stopped once.
    if (stopInFlightRef.current) return;
    stopInFlightRef.current = true;
    // Recorder-lifecycle safety (PO 2026-08-04). Clear the hard-
    // ceiling watchdog — the natural endpoint has arrived.
    _clearListenWatchdog();
    // Dispose the VAD immediately so it can't fire again.
    try { vadRef.current && vadRef.current.stop(); } catch { /* noop */ }
    vadRef.current = null;
    await new Promise((resolve) => {
      rec.addEventListener("stop", resolve, { once: true });
      try { rec.stop(); } catch { resolve(); }
    });
    recRef.current = null;
    const mime = rec.mimeType || "audio/webm";
    const chunks = chunksRef.current; chunksRef.current = [];
    if (!chunks.length) {
      // Recorder-lifecycle defect fix (PO 2026-08-04 rounds 2 + 3).
      // If the last server reply was a farewell
      // (``conversation_control === "end"``) the conversation has
      // already ended semantically. An empty-chunk capture that
      // arrives during or right after the goodbye TTS must NOT
      // surface "I didn't catch that" — release the mic silently
      // and stay idle. Also treat ``conversationActiveRef=false`` as
      // an implicit end so a race where endConversation already ran
      // (but the empty-chunk sequence was already queued) still
      // yields silently instead of re-arming through the retry cue.
      const _lastEx = lastExtraRef.current || {};
      if (_lastEx.conversation_control === "end"
          || !conversationActiveRef.current) {
        try { console.log("[7C emptyChunks] farewell in flight — no reprompt"); } catch { /* noop */ }
        emptyChunkRetriesRef.current = 0;
        try { endConversation({ reason: "voice" }); } catch { /* noop */ }
        return;
      }
      // Continuous Conversation contract (PO 2026-08-02 correction).
      // An empty capture must NOT return the UI to Tap-to-Speak
      // while the conversation is still active. Auto re-arm the mic
      // up to MAX_EMPTY_CHUNK_RETRIES; then yield gracefully.
      const nextCount = (emptyChunkRetriesRef.current || 0) + 1;
      emptyChunkRetriesRef.current = nextCount;
      const cue = lang === "es"
        ? "No detecté nada. Adelante, cuando quieras."
        : "I didn't catch that. Go ahead when you're ready.";
      const ceilingCue = lang === "es"
        ? "No detecté nada. Cuando quieras, toca el micrófono."
        : "I didn't catch that. Tap the mic when you're ready.";
      // Guards: don't auto-retry if the loop has already ended,
      // permission was lost, tab is hidden, or the retry ceiling was
      // reached. Any of those routes cleanly to Tap-to-Speak.
      const _hidden = typeof document !== "undefined" && document.hidden;
      const _perm = permissionRef.current;
      const _shouldRetry = (
        conversationActiveRef.current
        && !_hidden
        && _perm !== "denied"
        && nextCount <= MAX_EMPTY_CHUNK_RETRIES
      );
      if (_shouldRetry) {
        try { console.log("[7C emptyChunks] auto re-arm, retry", nextCount); } catch { /* noop */ }
        setError(cue);
        setStatus("idle");
        // Speak a soft cue so the contractor hears the retry
        // invitation. When TTS ends, ``_onSpokenEnd`` will re-arm
        // the mic (conversationActive is still true, no end signal).
        try {
          speakLeviReply(cue, lang || "en", {
            source: "empty_chunk_retry", mode: "command", status: "idle",
            onEnd: () => {
              try {
                if (conversationActiveRef.current
                    && !document.hidden
                    && permissionRef.current !== "denied"
                    && statusRef.current !== "listening"
                    && startListeningRef.current) {
                  startListeningRef.current();
                } else {
                  try { _clearInactivityTimer(); } catch { /* noop */ }
                }
              } catch { /* noop */ }
            },
          });
        } catch {
          // Fallback: no TTS available (headless / permission).
          // Arm directly after a short delay so the contractor sees
          // the mic re-open without needing the cue.
          // PO 2026-08-13 Boundary A · capture setTimeout id so the
          // unmount cleanup can clear it.
          try { if (emptyChunkArmTimerRef.current) clearTimeout(emptyChunkArmTimerRef.current); } catch { /* noop */ }
          emptyChunkArmTimerRef.current = setTimeout(() => {
            emptyChunkArmTimerRef.current = null;
            try {
              if (conversationActiveRef.current
                  && !document.hidden
                  && permissionRef.current !== "denied"
                  && statusRef.current !== "listening"
                  && startListeningRef.current) {
                startListeningRef.current();
              }
            } catch { /* noop */ }
          }, 250);
        }
        // Keep the idle ceiling ticking so a silent contractor
        // eventually closes the loop naturally.
        try { _resetInactivityTimer(); } catch { /* noop */ }
        return;
      }
      // Retry ceiling reached (or an ending guard fired). Yield
      // gracefully to Tap-to-Speak.
      try { console.log("[7C emptyChunks] retry ceiling — yielding to Tap-to-Speak"); } catch { /* noop */ }
      emptyChunkRetriesRef.current = 0;
      setStatus("idle");
      setError(ceilingCue);
      return;
    }
    // A non-empty capture — reset the retry counter.
    emptyChunkRetriesRef.current = 0;
    setStatus("uploading");
    const blob = new Blob(chunks, { type: mime });
    const ext = mime.includes("mp4") ? "m4a" : "webm";
    const file = new File([blob], `levi-cmd-${Date.now()}.${ext}`, { type: mime });
    await submit({ file });
  }, [submit, lang, _clearInactivityTimer, _resetInactivityTimer, _clearListenWatchdog, endConversation]);
  // Ref indirection so the VAD callback captured at listen-start can
  // still invoke the latest stopAndSend closure.
  const stopAndSendRef = useRef(null);
  useEffect(() => { stopAndSendRef.current = stopAndSend; }, [stopAndSend]);
  // Phase 7-B · ref indirection for startListening so the async
  // TTS-onEnd handler (captured at submit-time) can re-arm the mic
  // even after startListening's identity has changed on re-render.
  const startListeningRef = useRef(null);
  useEffect(() => { startListeningRef.current = startListening; },
                    [startListening]);

  const confirm = useCallback(async () => {
    return submit({ text: lang === "es" ? "sí" : "yes" });
  }, [submit, lang]);
  const cancel = useCallback(async () => {
    return submit({ text: lang === "es" ? "no" : "no" });
  }, [submit, lang]);

  // Phase 7-B · safe degradation, refined 2026-08-09 (P0 stabilization).
  // Prior behavior treated ANY visibilitychange → hidden as
  // abandonment and immediately called endConversation. iOS 18.7
  // Safari fires hidden for transient events (screen dim, control
  // center, permission dialog, camera prompt) that are NOT real
  // abandonment. The forensic audit proved this destroys live
  // sessions the contractor is still actively working.
  //
  // Correction: debounce the hidden→endConversation path by 2500 ms.
  // If the tab returns to visible within the debounce window, cancel
  // the shutdown AND attempt to reacquire the screen wake lock.
  // Only a sustained hidden state past the debounce is treated as
  // genuine backgrounding.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    let debounceTimer = null;
    const HIDDEN_DEBOUNCE_MS = 2500;
    const onVisibility = () => {
      const _hidden = !!document.hidden;
      setTabHidden(_hidden);
      try {
        pushSpeechDiag("levi:visibilitychange", {
          visibilityState: (typeof document !== "undefined" ? document.visibilityState : null),
          hidden: _hidden,
          conversationActive: !!conversationActiveRef.current,
          debounced: true,
        });
      } catch { /* diagnostic must never throw */ }
      if (_hidden) {
        if (!conversationActiveRef.current) return;
        // Start / restart the debounce. Only if we remain hidden
        // for the full window AND no active work is in flight do
        // we treat this as real abandonment.
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          debounceTimer = null;
          if (typeof document !== "undefined"
              && document.hidden
              && conversationActiveRef.current) {
            // P0 stabilization 2026-08-09 · at debounce expiry,
            // re-check the AUTHORITATIVE active-work state. Real-
            // iPhone evidence shows Safari can remain hidden > 2.5 s
            // while the contractor is still actively speaking. In
            // that case, do NOT end the conversation — suspend the
            // shutdown and keep the session alive. Only the 5-minute
            // TRUE-IDLE ceiling may legitimately terminate active
            // work, and its fire-time guard already handles that.
            let _active = { active: false, reason: null };
            try { _active = _leviIsActiveWork(); } catch { /* noop */ }
            if (_active.active) {
              try {
                pushSpeechDiag("visibility:shutdown_suppressed", {
                  reason: _active.reason,
                  hidden_since_ms_estimate: HIDDEN_DEBOUNCE_MS,
                });
              } catch { /* noop */ }
              // Do not schedule a new timeout. When the tab returns
              // to visible we'll cancel this handler entirely. The
              // 5-minute TRUE-IDLE ceiling still runs in the
              // background; if the contractor genuinely goes idle,
              // it will end the conversation cleanly.
              return;
            }
            try { pushSpeechDiag("levi:visibility_debounce_expired", {}); } catch { /* noop */ }
            endConversation({ reason: "tab_hidden" });
          }
        }, HIDDEN_DEBOUNCE_MS);
      } else {
        // Visible return. Cancel any pending shutdown and reacquire
        // wake lock if the conversation is still alive.
        if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null; }
        if (conversationActiveRef.current) {
          try { pushSpeechDiag("wakeLock:reacquire_attempt", { source: "visible_return" }); } catch { /* noop */ }
          try { acquireWakeLock("visible_return").catch(() => {}); } catch { /* noop */ }
        }
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null; }
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [endConversation]);

  useEffect(() => {
    // Query permission once at mount; observe change events.
    if (typeof navigator === "undefined" || !navigator.permissions
        || !navigator.permissions.query) return undefined;
    let cancelled = false;
    let statusObj = null;
    (async () => {
      try {
        const s = await navigator.permissions.query({ name: "microphone" });
        if (cancelled) return;
        statusObj = s;
        permissionRef.current = s.state;
        const onChange = () => {
          permissionRef.current = s.state;
          setMicPermissionDenied(s.state === "denied");
          if (s.state === "denied" && conversationActiveRef.current) {
            endConversation({ reason: "permission_revoked" });
          }
        };
        s.addEventListener && s.addEventListener("change", onChange);
      } catch { /* noop */ }
    })();
    return () => {
      cancelled = true;
      try { statusObj && statusObj.removeEventListener
             && statusObj.removeEventListener("change", () => {}); }
      catch { /* noop */ }
    };
  }, [endConversation]);

  // Cleanup on unmount (drawer close, page unload).
  useEffect(() => {
    return () => {
      _clearInactivityTimer();
      _releaseMic();
      // PO 2026-08-13 · Duplicate-owner lifecycle repair.
      //
      // Clear every delayed callback that could otherwise fire AFTER
      // this hook has unmounted and invoke ``startListeningRef.current()``
      // or ``submit()`` on the outgoing instance's closures. Without
      // these clears the outgoing instance can resurrect its mic /
      // VAD / MediaRecorder lifecycle after a fresh hook instance has
      // already mounted on the new route — the exact "two Levi voices
      // fighting" pattern the Product Owner heard on iPhone.
      try {
        if (phase7cArmTimerRef.current) {
          clearTimeout(phase7cArmTimerRef.current);
          phase7cArmTimerRef.current = null;
        }
      } catch { /* noop */ }
      try {
        if (emptyChunkArmTimerRef.current) {
          clearTimeout(emptyChunkArmTimerRef.current);
          emptyChunkArmTimerRef.current = null;
        }
      } catch { /* noop */ }
      try {
        if (postWalkArmTimerRef.current) {
          clearTimeout(postWalkArmTimerRef.current);
          postWalkArmTimerRef.current = null;
        }
      } catch { /* noop */ }
      try {
        if (bufferedPostWalkArmTimerRef.current) {
          clearTimeout(bufferedPostWalkArmTimerRef.current);
          bufferedPostWalkArmTimerRef.current = null;
        }
      } catch { /* noop */ }
      // PO 2026-08-13 · Dispose any residual barge-in detector so a
      // module-level audio callback cannot invoke ``onFire`` (which
      // resolves to ``startListeningRef.current()``) on the outgoing
      // instance's closures after unmount. Behavior while mounted is
      // unchanged — ``_armBargeIn`` disposes and re-arms as usual.
      try { _disposeBargeIn(); } catch { /* noop */ }
      // PO 2026-08-13 · Best-effort break of the ref indirection
      // used by delayed callbacks captured earlier in this hook's
      // lifetime. If any callback outside our control somehow still
      // holds a reference to this ref (e.g. from within a promise
      // chain not yet tracked here), nulling it converts a would-be
      // resurrection into a silent no-op instead of a live second
      // owner.
      try { startListeningRef.current = null; } catch { /* noop */ }
      try { stopAndSendRef.current = null; } catch { /* noop */ }
      // B (PO 2026-08-07) · release any held Screen Wake Lock on
      // hook unmount (drawer close / page unload / route change).
      try { pushSpeechDiag("wakeLock:release_context", { source: "unmount" }); } catch { /* noop */ }
      try { releaseWakeLock("unmount"); } catch { /* noop */ }
    };
  }, [_clearInactivityTimer, _releaseMic, _disposeBargeIn]);

  // Vision Conversational Deployment · Checkpoint 1 · D-4 mic
  // arbitration (PO 2026-08-05). When ``useVideoWalkSession``
  // starts recording, it dispatches ``levi:vision-walk-started`` on
  // window. Release the Levi drawer's microphone and pause the
  // auto-listen loop so the Video Walk's MediaRecorder owns the
  // input stream. The Levi conversation surface (drawer state,
  // turns, pending_action, vision_objective) is preserved intact —
  // only the drawer's live mic loop is released.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onWalkStart = (ev) => {
      try {
        // Checkpoint 2 · capture the active Video Walk session id.
        const sid = ev && ev.detail && ev.detail.session_id;
        if (sid) activeVisionSessionIdRef.current = sid;
        // Vision Conversational Deployment · C1 · when the live-
        // conversation feature is ON, DO NOT release the drawer. The
        // Vision MediaStream is now owned by useVideoWalkSession and
        // is available via window.__leviVisionMediaStream. The
        // drawer's next startListening will adopt it (see shared-
        // stream adopt branch). Wake lock stays held. On flag OFF
        // the legacy record-and-summarize behavior is preserved
        // exactly.
        if (isVisionLiveConversationEnabled()) {
          try { console.log("[C1 walk-start] flag ON — Levi conversation preserved"); } catch { /* noop */ }
          // Keep wake lock alive proactively so the walkthrough stays
          // awake even if the drawer had not previously acquired it.
          try { acquireWakeLock("vision_walk_started"); } catch { /* noop */ }
          return;
        }
        if (conversationActiveRef.current) {
          endConversation({ reason: "vision_walk_started" });
        }
      } catch { /* noop */ }
    };
    // Stitch 3 · Session-scoped post-walk continuity (PO 2026-08-08).
    // Routes through the same `submit()` pipeline the drawer uses for
    // every /command turn — so the Levi acknowledgement + summary
    // renders in the SAME unified conversation surface. Open the
    // drawer so a mobile contractor who left the drawer closed sees
    // Levi speak the moment Finish Walkthrough completes.
    const continuityDeliveredRef = { current: new Set() };
    const onWalkStopped = (ev) => {
      activeVisionSessionIdRef.current = null;
      // Vision Conversational Deployment · C1 · clear ownership so
      // subsequent startListening opens a Levi-owned microphone
      // stream as usual. Also null the streamRef if it still points
      // at the (now-stopped) Vision stream so _hasHealthyStream()
      // reports false and the next mic tap opens a fresh stream.
      try {
        if (visionOwnedStreamRef.current) {
          visionOwnedStreamRef.current = false;
          streamRef.current = null;
        }
      } catch { /* noop */ }
      const d = (ev && ev.detail) || {};
      const wsid = d.walk_session_id;
      const lsid = d.levi_estimate_session_id;
      if (!wsid || !lsid || !d.finalized) return;
      if (continuityDeliveredRef.current.has(wsid)) return;
      continuityDeliveredRef.current.add(wsid);
      try {
        // Open the unified Levi drawer so the contractor sees Levi
        // acknowledge completion instead of being left on the summary
        // card. openDrawerRef is captured from the LeviDrawerProvider
        // ancestor via useLeviDrawer at hook init.
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("levi:auto-open-drawer", {
            detail: { reason: "vision_walk_completed" },
          }));
        }
      } catch { /* noop */ }
      // Slight delay so the drawer's LeviCommandConsole is mounted
      // before we submit — otherwise the reply lands in a hook state
      // the drawer has not yet subscribed to.
      // PO 2026-08-13 Boundary A · capture setTimeout id so the
      // unmount cleanup can clear it and this post-walk submit does
      // not fire from an outgoing hook instance after remount.
      try { if (postWalkArmTimerRef.current) clearTimeout(postWalkArmTimerRef.current); } catch { /* noop */ }
      postWalkArmTimerRef.current = setTimeout(async () => {
        postWalkArmTimerRef.current = null;
        try {
          const reply = await submit({ walkCompletedSessionId: wsid });
          // Post-walk continuity re-arm (PO 2026-08-09). When Levi's
          // walk-completed reply expects a contractor response (asked
          // a material gap OR asked about starting the estimate),
          // activate the conversation loop so the mic auto-arms after
          // Levi finishes speaking. The 8-second no-voice window in
          // leviVAD governs the ceiling — if no speech is detected,
          // the recorder stops naturally and the contractor can tap
          // or type. Typing remains available at all times.
          try {
            const expects = reply && (reply.expects_reply === true
              || reply.action_type === "vision_walk_summary");
            if (expects) {
              setConversationActive(true);
              conversationActiveRef.current = true;
              setInactivityNotice(false);
            }
          } catch { /* noop */ }
        } catch (e) {
          console.warn("[LEVI-POST-WALK] submit failed:", e);
        }
      }, 250);
    };
    window.addEventListener("levi:vision-walk-started", onWalkStart);
    window.addEventListener("levi:vision-walk-stopped", onWalkStopped);

    // P0 RC-2 fix (PO 2026-08-09 real-iPhone reproduction). Mount-
    // time consumer for the provider-level ``window.__leviPendingPostWalk``
    // buffer. If the recorder finalized while the drawer was closed
    // (dedicated Vision page workflow — most common on iPhone), the
    // LeviDrawerProvider will have opened the drawer and stashed the
    // walk payload here. Consume it exactly once and submit through
    // the SAME pipeline as the in-drawer path.
    try {
      const pending = (typeof window !== "undefined")
        ? window.__leviPendingPostWalk : null;
      if (pending && pending.walk_session_id
          && !continuityDeliveredRef.current.has(pending.walk_session_id)) {
        continuityDeliveredRef.current.add(pending.walk_session_id);
        try { window.__leviPendingPostWalk = null; } catch { /* noop */ }
        // PO 2026-08-13 Boundary A · capture setTimeout id so the
        // unmount cleanup can clear it. Prevents the outgoing hook
        // instance from submitting the buffered post-walk turn after
        // navigation/remount.
        try { if (bufferedPostWalkArmTimerRef.current) clearTimeout(bufferedPostWalkArmTimerRef.current); } catch { /* noop */ }
        bufferedPostWalkArmTimerRef.current = setTimeout(async () => {
          bufferedPostWalkArmTimerRef.current = null;
          try {
            const reply = await submit({
              walkCompletedSessionId: pending.walk_session_id,
            });
            try {
              const expects = reply && (reply.expects_reply === true
                || reply.action_type === "vision_walk_summary");
              if (expects) {
                setConversationActive(true);
                conversationActiveRef.current = true;
                setInactivityNotice(false);
              }
            } catch { /* noop */ }
          } catch (e) {
            console.warn("[LEVI-POST-WALK] buffered submit failed:", e);
          }
        }, 300);
      }
    } catch { /* noop */ }
    return () => {
      window.removeEventListener("levi:vision-walk-started", onWalkStart);
      window.removeEventListener("levi:vision-walk-stopped", onWalkStopped);
    };
  }, [endConversation, submit]);

  // C-2 · include the speaking-state tick as a memo input so the
  // presence useMemo re-runs at real-time speech-start / speech-end
  // boundaries. `leviIsSpeaking()` inside the memo remains the
  // authoritative source of truth passed to `derivePresence`; the
  // tick's only role is to invalidate the memo cache. Referenced
  // once inside the memo body (as a diag-only field) so the
  // exhaustive-deps rule accepts it as a dependency.
  return useMemo(() => {
    // Phase 7-C · presence derived deterministically. Precedence:
    // ended > listening > thinking > speaking > standby. The
    // `muted` state was retired after PO real-world QA (2026-02).
    let _isSpeaking = false;
    try { _isSpeaking = leviIsSpeaking(); } catch { _isSpeaking = false; }
    // C-2 · diag-only reference to satisfy exhaustive-deps. The
    // tick's actual role is memo invalidation; `leviIsSpeaking()`
    // above stays authoritative for presence derivation.
    const _bridgeTickValue = _speakingTick;
    const presence = derivePresence({
      conversationActive,
      drawerOpen,
      tabHidden,
      micPermissionDenied,
      isSpeaking: _isSpeaking,
      status,
    });
    return {
      sessionId, status, error, turns, pendingAction, lastVisible, lastMatches, lastExtra,
      startListening, stopAndSend, submitText: (t, opts) => submit({ text: t, ...(opts || {}) }), confirm, cancel,
      // Phase 7-B · Conversation Flow surface.
      conversationActive, inactivityNotice,
      pauseConversation, endConversation,
      // Phase 7-C · Live Voice surface (mute retired).
      presence,
      setDrawerOpen,
      // P0 Tier-2 (PO 2026-08-09) · read-only accessor to the
      // current microphone MediaStream. Consumed by
      // LeviListeningVisualizer to source its live waveform +
      // synthetic captureStream. Does NOT clone or take ownership;
      // returns whatever `streamRef.current` currently points at.
      getMicStream: () => streamRef.current,
      // P0 (PO 2026-08-09) · read-only accessors that pipe the VAD's
      // single WebAudio observation branch out to the visualizer.
      // Prevents a second `MediaStreamAudioSourceNode` from starving
      // the VAD analyser on iOS Safari (root cause of the false
      // silence_after_voice endpoint during long narration).
      getVadWaveform: (out) => {
        try {
          const v = vadRef.current;
          return (v && typeof v.getWaveformSnapshot === "function")
            ? v.getWaveformSnapshot(out) : null;
        } catch { return null; }
      },
      getVadFftSize: () => {
        try {
          const v = vadRef.current;
          return (v && typeof v.getAnalyserFftSize === "function")
            ? v.getAnalyserFftSize() : 0;
        } catch { return 0; }
      },
      getVadDiagnostics: () => {
        try {
          const v = vadRef.current;
          return (v && typeof v.getDiagnostics === "function")
            ? v.getDiagnostics() : null;
        } catch { return null; }
      },
      // C-2 · diagnostic exposure of the speaking-state bridge tick
      // so integration tests and the diag panel can verify the
      // subscription has fired. Not part of any conversation-state
      // machine. Consumers should still use `presence` for UI.
      _speakingBridgeTick: _bridgeTickValue,
    };
  }, [sessionId, status, error, turns, pendingAction, lastVisible, lastMatches, lastExtra,
        startListening, stopAndSend, submit, confirm, cancel,
        conversationActive, inactivityNotice,
        pauseConversation, endConversation,
        drawerOpen, tabHidden, micPermissionDenied,
        // C-2 · include the speaking-state tick so the presence
        // badge re-renders at real-time speech-start / speech-end
        // boundaries. `leviIsSpeaking()` inside the memo remains the
        // authoritative value passed to `derivePresence`. The
        // exhaustive-deps lint reports this dep as "unnecessary"
        // because it does not appear in the memo body — that is
        // exactly the design: the value is only used to invalidate
        // the memo cache when Levi starts or stops speaking.
        _speakingTick]);
}
